import React, { useState, useEffect, useRef, useCallback } from "react";
import toast from 'react-hot-toast';
import {
  ChevronLeft, ChevronRight, Search, Bell, Plus, ArrowLeft,
  Eye, Pencil, Filter, Upload, AlertCircle, AlertTriangle, X,
  ChevronDown, Users, UserCheck, UserMinus, User, Briefcase,
  Phone, Settings, Building2, FileText, Trash2,
  History, Clock, Mail, MapPin, Building, CheckCircle2,
  Download, Paperclip, Save, Check, AlertOctagon, Info,
  FileSpreadsheet, RefreshCw, Camera, Droplets, CreditCard, Wallet, GraduationCap, BookOpen
} from "lucide-react";
import "../styles/employeemaster.css";
import MainLayout from "./MainLayout";
import { useAuth } from "../context/AuthContext";

// ── API Base ──────────────────────────────────────────────────────────────────
const BASE = process.env.REACT_APP_API_BASE_URL;

// ── API Helpers ───────────────────────────────────────────────────────────────
const authHeaders = (token) => ({
  Authorization: `Bearer ${token}`,
});


async function apiFetch(url, options = {}) {
  const res = await fetch(url, options);
  const json = await res.json();
  if (!res.ok) throw new Error(json.message || `HTTP ${res.status}`);
  return json.data !== undefined ? json.data : json;
}

// ── Employee APIs ─────────────────────────────────────────────────────────────
const api = {
  getEmployees: (params, token) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v && v !== "all"))
    ).toString();
    return apiFetch(`${BASE}/api/employee-master${qs ? `?${qs}` : ""}`, {
      headers: authHeaders(token),
    });
  },
  getEmployee: (id, token) =>
    apiFetch(`${BASE}/api/employee-master/${id}`, { headers: authHeaders(token) }),

  createEmployee: (payload, token) =>
    apiFetch(`${BASE}/api/employee-master`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders(token) },
      body: JSON.stringify(payload),
    }),

  updateEmployee: (id, payload, token) =>
    apiFetch(`${BASE}/api/employee-master/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...authHeaders(token) },
      body: JSON.stringify(payload),
    }),

  deleteEmployee: (id, token) =>
    apiFetch(`${BASE}/api/employee-master/${id}`, {
      method: "DELETE",
      headers: authHeaders(token),
    }),

  getWorkHistory: (empId, token) =>
    apiFetch(`${BASE}/api/employee-master/${empId}/work-history`, {
      headers: authHeaders(token),
    }),

  createWorkHistory: (empId, payload, token) =>
    apiFetch(`${BASE}/api/employee-master/${empId}/work-history`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders(token) },
      body: JSON.stringify(payload),
    }),

  updateWorkHistory: (empId, whId, payload, token) =>
    apiFetch(`${BASE}/api/employee-master/${empId}/work-history/${whId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...authHeaders(token) },
      body: JSON.stringify(payload),
    }),

  deleteWorkHistory: (empId, whId, token) =>
    apiFetch(`${BASE}/api/employee-master/${empId}/work-history/${whId}`, {
      method: "DELETE",
      headers: authHeaders(token),
    }),

  getDocuments: (empId, token) =>
    apiFetch(`${BASE}/api/employee-master/${empId}/documents`, {
      headers: authHeaders(token),
    }),

  uploadDocument: (empId, file, docType, docLabel, token) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("docType", docType);
    fd.append("docLabel", docLabel);
    return apiFetch(`${BASE}/api/employee-master/${empId}/documents`, {
      method: "POST",
      headers: authHeaders(token),
      body: fd,
    });
  },

  uploadWorkHistoryDoc: (empId, whId, file, docType, docLabel, token) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("docType", docType);
    fd.append("docLabel", docLabel);
    return apiFetch(`${BASE}/api/employee-master/${empId}/work-history/${whId}/documents`, {
      method: "POST",
      headers: authHeaders(token),
      body: fd,
    });
  },

  deleteDocument: (empId, docId, token) =>
    apiFetch(`${BASE}/api/employee-master/${empId}/documents/${docId}`, {
      method: "DELETE",
      headers: authHeaders(token),
    }),

  uploadProfilePic: (empId, file, token) => {
    const fd = new FormData();
    fd.append("file", file);
    return apiFetch(`${BASE}/api/employee-master/${empId}/profile-picture`, {
      method: "POST",
      headers: authHeaders(token),
      body: fd,
    });
  },

  deleteProfilePic: (empId, token) =>
    apiFetch(`${BASE}/api/employee-master/${empId}/profile-picture`, {
      method: "DELETE",
      headers: authHeaders(token),
    }),

  getDropdown: async (key, token) => {
    const res = await fetch(`${BASE}/api/dropdowns/values/${key}`, {
      headers: authHeaders(token),
    });
    if (!res.ok) throw new Error(`Dropdown fetch failed: ${key}`);
    const json = await res.json();
    return Array.isArray(json) ? json : (json.data || []);
  },

  createEducation: (empId, payload, token) =>
    apiFetch(`${BASE}/api/employee-master/${empId}/education`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders(token) },
      body: JSON.stringify(payload),
    }),

  deleteEducation: (empId, eduId, token) =>
    apiFetch(`${BASE}/api/employee-master/${empId}/education/${eduId}`, {
      method: "DELETE",
      headers: authHeaders(token),
    }),

  getDepartments: (token) =>
    apiFetch(`${BASE}/api/department-master`, {
      headers: authHeaders(token),
    }),

  getDesignations: (token) =>
    apiFetch(`${BASE}/api/designation-master`, {
      headers: authHeaders(token),
    }),

  getShifts: (token) =>
    apiFetch(`${BASE}/api/shift-master`, {
      headers: authHeaders(token),
    }),
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const AVATAR_PALETTE = [
  { bg: "#dbeafe", color: "#1d4ed8" },
  { bg: "#dcfce7", color: "#15803d" },
  { bg: "#fef9c3", color: "#a16207" },
  { bg: "#fce7f3", color: "#be185d" },
  { bg: "#ede9fe", color: "#7c3aed" },
  { bg: "#ffedd5", color: "#c2410c" },
];

const getAvatar = (id) => {
  const num = String(id).split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  return AVATAR_PALETTE[num % AVATAR_PALETTE.length];
};

const getInitials = (emp) =>
  `${(emp.first_name || emp.firstName || "?")[0]}${(emp.last_name || emp.lastName || "?")[0]}`.toUpperCase();

const fmtDateISO = (d) => {
  if (!d) return "";
  const date = new Date(d);
  if (isNaN(date.getTime())) return "";
  return date.toISOString().split("T")[0];
};

const fullName = (emp) =>
  [emp.first_name || emp.firstName, emp.middle_name || emp.middleName || emp.Middle_Name, emp.last_name || emp.lastName]
    .filter(Boolean)
    .join(" ");

const normalizeDropdownItems = (raw) => {
  if (!Array.isArray(raw)) return [];
  return raw.map(item => ({
    value: item.dropdown_value_code ?? item.key ?? item.value ?? item.id ?? item.code ?? item.department_id ?? item.designation_id ?? String(item),
    label: item.dropdown_value_name ?? item.label ?? item.name ?? item.display ?? item.department_name ?? item.designation_name ?? String(item),
  }));
};

// ── FIX 1: resolveLabel — resolves a code/value to its display label ──────────
// Tries to match by value first, then by label (in case API already returns name)
const resolveLabel = (value, options) => {
  if (!value) return value;
  if (!options?.length) return value;
  const match = options.find(
    (o) => o.value === value || o.label === value
  );
  return match ? match.label : value;
};

const normalizeArrayValue = (value) => {
  if (value == null) return [];
  if (Array.isArray(value)) return value;
  return String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const normalizeEmployee = (e) => ({
  ...e,
  id: e.employee_id || e.id,
  employeeId: e.employee_id || e.employeeId,
  employeeCode: e.employee_code || e.employeeCode || "",
  firstName: e.first_name || e.firstName || "",
  middleName: e.Middle_Name || e.middle_name || e.middleName || "",
  lastName: e.last_name || e.lastName || "",
  gender: e.gender || "",
  dateOfBirth: fmtDateISO(e.date_of_birth || e.dateOfBirth),
  bloodGroup: e.blood_group || e.bloodGroup || "",
  maritalStatus: e.marital_status || e.maritalStatus || "",
  phoneNumber: e.phone_number || e.phoneNumber || "",
  email: e.email || "",
  officeEmail: e.office_email || e.officeEmail || "",
  alternatePhone: e.alternate_phone || e.alternatePhone || "",
  officePhone: e.office_phone || e.officePhone || "",
  employmentStatus: e.employment_status || e.employmentStatus || e.status || "Active",
  employmentType: e.employment_type || e.employmentType || "",
  joiningDate: fmtDateISO(e.joining_date || e.joiningDate),
  probationEndDate: fmtDateISO(e.probation_end_date || e.probationEndDate),
  exitDate: fmtDateISO(e.exit_date || e.exitDate),
  exitReason: e.exit_reason || e.exitReason || "",
  exitRemarks: e.exit_remarks || e.exitRemarks || "",
  department: e.department || "",
  designation: e.designation || "",
  role: e.role || "",
  managerId: e.manager_id || e.managerId || "",
  leadEmail: e.lead_email || e.leadEmail || "",
  reportingHeadEmail: e.reporting_head_email || e.reportingHeadEmail || "",
  hrEmails: normalizeArrayValue(e.hr_emails || e.hrEmails),
  leadEmployeeId: e.lead_employee_id || e.lead_id || e.leadId || "",
  reportingHeadEmployeeId: e.reporting_head_employee_id || e.reporting_head_id || e.reportingHeadId || "",
  hrEmployeeIds: normalizeArrayValue(e.hr_employee_ids || e.hr_ids || e.hrIds),
  statusReason: e.status_reason || e.statusReason || "",
  workMode: e.work_mode || e.workMode || "",
  workLocation: e.work_location || e.workLocation || "",
  shiftTiming: e.shift_timing || e.shiftTiming || "",
  timeZone: e.time_zone || e.timeZone || "Asia/Kolkata",
  businessUnit: e.business_unit || e.businessUnit || "",
  team: e.team || "",
  region: e.region || "",
  client: e.client || "",
  addressLine1: e.address_line1 || e.addressLine1 || "",
  addressLine2: e.address_line2 || e.addressLine2 || "",
  city: e.city || "",
  state: e.state || "",
  country: e.country || "India",
  postalCode: e.postal_code || e.postalCode || "",
  emergencyContactName: e.emergency_contact_name || e.emergencyContactName || "",
  emergencyContactNumber: e.emergency_contact_number || e.emergencyContactNumber || "",
  domainUsername: e.domain_username || e.domainUsername || "",
  assignedAssets: e.assigned_assets || e.assignedAssets || "",
  notes: e.notes || "",
  profilePicUrl: e.profile_pic_url || e.profilePicUrl || "",
  panNumber: e.pan_number || e.panNumber || "",
  aadhaarNumber: e.aadhaar_number || e.aadhaarNumber || "",
  esiNumber: e.esi_number || e.esiNumber || "",
  uanNumber: e.uan_number || e.uanNumber || "",
  bankName: e.bank_name || e.bankName || "",
  ifscCode: e.ifsc_code || e.ifscCode || "",
  accountNumber: e.account_number || e.accountNumber || "",
  branch: e.bank_branch || e.branch || "",
  educationHistory: (() => {
    // Parse education_history JSONB from backend
    const raw = e.education_history || e.educationHistory;
    if (Array.isArray(raw)) return raw;
    if (typeof raw === "string") { try { return JSON.parse(raw); } catch { return []; } }
    return [];
  })(),
  documents: e.documents || [],
  workHistory: e.workHistory || [],
});

const STATUS_CONFIG = {
  active: { label: "Active", cls: "em-badge--active" },
  inactive: { label: "Inactive", cls: "em-badge--inactive" },
  "on-leave": { label: "On Leave", cls: "em-badge--onleave" },
  terminated: { label: "Terminated", cls: "em-badge--inactive" },
};

const getStatusConfig = (status) => {
  if (!status) return null;
  const key = String(status).toLowerCase();
  return STATUS_CONFIG[key] || { label: status, cls: "em-badge--outline" };
};

const WORK_MODE_LABEL = {
  wfh: "Remote", office: "Office", hybrid: "Hybrid",
  "Work from Home": "Remote", "Office": "Office", "Hybrid": "Hybrid",
  "WFH": "Remote", "OFFICE": "Office", "HYBRID": "Hybrid",
};

const ETYPE_LABEL = {
  "full-time": "Full Time", "part-time": "Part Time",
  contract: "Contract", intern: "Internship", freelance: "Freelance",
  "Full-time": "Full Time", "Contract": "Contract", "Intern": "Internship",
  "EMPLOYEE": "Employee", "Full Time": "Full Time", "Part Time": "Part Time",
};

// Static fallback arrays (used only if API dropdown returns empty)
const DEPARTMENTS = ["Engineering", "Product", "Design", "Sales", "HR", "Finance", "Marketing", "Operations"];
const DESIGNATIONS = ["Software Engineer", "Senior Software Engineer", "Engineering Manager", "Product Manager", "Senior Designer", "Sales Executive", "HR Manager", "Finance Lead", "Team Lead"];

const ITEMS_PER_PAGE = 10;
const ICON_SM = { width: 14, height: 14 };
const ICON_XS = { width: 11, height: 11 };

const QUALIFICATIONS = [
  { value: "SSLC", label: "SSLC (10th)" },
  { value: "HSC", label: "HSC (12th)" },
  { value: "Diploma", label: "Diploma" },
  { value: "UG", label: "Under Graduate" },
  { value: "PG", label: "Post Graduate" },
  { value: "Doctorate", label: "Doctorate" },
  { value: "Certification", label: "Certification" },
  { value: "Other", label: "Other" },
];

const DOC_TYPES = [
  { value: "pan", label: "PAN Card" },
  { value: "aadhaar", label: "Aadhaar Card" },
  { value: "joining", label: "Joining Letter" },
  { value: "offer", label: "Offer Letter" },
  { value: "relieving", label: "Relieving Letter" },
  { value: "edu_cert", label: "Educational Certificate" },
  { value: "exp_cert", label: "Experience Certificate" },
  { value: "resume", label: "Resume / CV" },
  { value: "address", label: "Address Proof" },
  { value: "bank", label: "Bank Details" },
  { value: "other", label: "Other" },
];

// ── useDropdowns hook ─────────────────────────────────────────────────────────
function useDropdowns(token) {
  const [dropdowns, setDropdowns] = useState({
    ROLE: [],
    EMPLOYMENT_STATUS: [],
    GENDERS: [],
    BLOOD_GROUP: [],
    MARITAL_STATUS: [],
    WORK_MODE: [],
    EMPLOYEE_TYPE: [],
    DESIGNATION: [],
    DEPARTMENT: [],
  });
  const [loadingDropdowns, setLoadingDropdowns] = useState(true);

  useEffect(() => {
    if (!token) return;

    const keysMap = {
      ROLE: "ROLE",
      EMPLOYMENT_STATUS: "EMPLOYMENT_STATUS",
      GENDERS: "GENDERS",
      BLOOD_GROUP: "BLOOD_GROUP",
      MARITAL_STATUS: "MARITAl_STATUS",
      WORK_MODE: "WORK_MODE",
      EMPLOYEE_TYPE: "EmployeeType",
    };

    const stateKeys = Object.keys(keysMap);
    const apiKeys = Object.values(keysMap);

    Promise.allSettled([
      ...apiKeys.map(k => api.getDropdown(k, token)),
      api.getDepartments(token),
      api.getDesignations(token),
      api.getShifts(token)
    ])
      .then(results => {
        const updated = {};
        stateKeys.forEach((stateKey, i) => {
          if (results[i].status === "fulfilled") {
            const raw = results[i].value;
            updated[stateKey] = normalizeDropdownItems(Array.isArray(raw) ? raw : []);
          } else {
            console.warn(`Dropdown failed: ${apiKeys[i]}`, results[i].reason);
            updated[stateKey] = [];
          }
        });

        // Handle Departments
        const deptIndex = stateKeys.length;
        if (results[deptIndex].status === "fulfilled") {
          updated.DEPARTMENT = normalizeDropdownItems(results[deptIndex].value);
        } else {
          console.warn("Departments failed:", results[deptIndex].reason);
          updated.DEPARTMENT = [];
        }

        // Handle Designations
        const desigIndex = stateKeys.length + 1;
        if (results[desigIndex].status === "fulfilled") {
          updated.DESIGNATION = normalizeDropdownItems(results[desigIndex].value);
        } else {
          console.warn("Designations failed:", results[desigIndex].reason);
          updated.DESIGNATION = [];
        }

        // Handle Shifts
        const shiftIndex = stateKeys.length + 2;
        if (results[shiftIndex].status === "fulfilled") {
          const shiftData = results[shiftIndex].value;
          const list = Array.isArray(shiftData) ? shiftData : (shiftData?.data || shiftData?.shifts || []);
          updated.SHIFTS = list.map(s => ({
            value: String(s.id),
            label: s.shift_name,
            startTime: s.start_time,
            endTime: s.end_time
          }));
        } else {
          console.warn("Shifts failed:", results[shiftIndex].reason);
          updated.SHIFTS = [];
        }

        setDropdowns(prev => ({ ...prev, ...updated }));
      })
      .finally(() => setLoadingDropdowns(false));
  }, [token]);

  return { dropdowns, loadingDropdowns };
}

// ── Duration helpers ──────────────────────────────────────────────────────────
function calcDuration(startDate, endDate) {
  if (!startDate) return null;
  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : new Date();
  let months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
  if (months < 0) months = 0;
  const yrs = Math.floor(months / 12);
  const mos = months % 12;
  if (yrs === 0) return `${mos} mo${mos !== 1 ? "s" : ""}`;
  if (mos === 0) return `${yrs} yr${yrs !== 1 ? "s" : ""}`;
  return `${yrs} yr${yrs !== 1 ? "s" : ""} ${mos} mo${mos !== 1 ? "s" : ""}`;
}

function monthsToStr(m) {
  if (m <= 0) return "—";
  const yrs = Math.floor(m / 12);
  const mos = m % 12;
  if (yrs === 0) return `${mos} month${mos !== 1 ? "s" : ""}`;
  if (mos === 0) return `${yrs} year${yrs !== 1 ? "s" : ""}`;
  return `${yrs} year${yrs !== 1 ? "s" : ""} ${mos} month${mos !== 1 ? "s" : ""}`;
}

// Returns "MM-YYYY" format
const fmtDate = (d) => {
  if (!d) return null;
  try {
    const date = new Date(d);
    if (isNaN(date.getTime())) return d;
    const local = new Date(date.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
    const month = String(local.getMonth() + 1).padStart(2, '0');
    const year = local.getFullYear();
    return `${month}-${year}`;
  } catch { return d; }
};

// Returns "DD-MM-YYYY" format
const fmtDateFull = (d) => {
  if (!d) return null;
  try {
    const date = new Date(d);
    if (isNaN(date.getTime())) return d;
    const local = new Date(date.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
    const day = String(local.getDate()).padStart(2, '0');
    const month = String(local.getMonth() + 1).padStart(2, '0');
    const year = local.getFullYear();
    return `${day}-${month}-${year}`;
  } catch { return d; }
};

const fmtDateTime = (d) => {
  if (!d) return "";
  try {
    const date = new Date(d);
    if (isNaN(date.getTime())) return d;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const strTime = String(hours).padStart(2, '0') + ':' + minutes + ' ' + ampm;
    return `${day}-${month}-${year} ${strTime}`;
  } catch { return d; }
};

// Returns date string YYYY-MM-DD that is 1 day before the given date
const oneDayBefore = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  d.setDate(d.getDate() - 1);
  return d.toISOString().split("T")[0];
};

// Returns date string YYYY-MM-DD that is 1 day after the given date
const oneDayAfter = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  d.setDate(d.getDate() + 1);
  return d.toISOString().split("T")[0];
};

// ── Confirm Modal ─────────────────────────────────────────────────────────────
function ConfirmModal({ isOpen, title, message, onConfirm, onCancel, confirmLabel = "Confirm", cancelLabel = "Cancel" }) {
  if (!isOpen) return null;
  return (
    <div className="em-modal-overlay" onClick={onCancel}>
      <div className="em-modal em-modal--sm" onClick={e => e.stopPropagation()}>
        <div className="em-modal-header">
          <div><h3>{title}</h3><p>{message}</p></div>
        </div>
        <div className="em-modal-footer">
          <button className="em-btn em-btn--outline em-btn--sm" onClick={onCancel}>{cancelLabel}</button>
          <button className="em-btn em-btn--primary em-btn--sm" onClick={onConfirm}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

// ── Dropdown ──────────────────────────────────────────────────────────────────
function Dropdown({ trigger, items }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);
  return (
    <div className="em-dropdown" ref={ref}>
      <div onClick={() => setOpen(o => !o)}>{trigger}</div>
      {open && (
        <div className="em-dropdown-menu">
          {items.map((item, i) => (
            <div key={i} className="em-dropdown-item" onClick={() => { item.onClick(); setOpen(false); }}>
              {item.icon}{item.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MultiSelectDropdown({ items, selectedValues, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  const values = Array.isArray(selectedValues) ? selectedValues : [];
  const selectedLabels = items.filter(item => values.includes(item.value)).map(item => item.label);
  const displayText = selectedLabels.length > 0 ? selectedLabels.join(", ") : placeholder || "Select...";

  const toggleValue = (value) => {
    const nextValues = values.includes(value)
      ? values.filter((v) => v !== value)
      : [...values, value];
    onChange(nextValues);
  };

  const filtered = items.filter(item =>
    item.label.toLowerCase().includes(search.toLowerCase()) ||
    item.value.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="em-dropdown em-multi-select" ref={ref}>
      <button type="button" className="em-input em-input--dropdown" onClick={() => setOpen((o) => !o)}>
        <span className={`em-multiselect-placeholder${selectedLabels.length ? " em-multiselect-has-value" : ""}`}>
          {displayText}
        </span>
        <ChevronDown size={14} className="em-multiselect-icon" />
      </button>
      {open && (
        <div className="em-dropdown-menu em-dropdown-menu--multi">
          <div className="em-dropdown-search-wrap">
            <Search size={13} className="em-dropdown-search-icon" />
            <input
              className="em-dropdown-search-input"
              autoFocus
              placeholder="Search by name or ID..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              onClick={e => e.stopPropagation()}
            />
          </div>
          <div className="em-dropdown-options-list">
            {filtered.length === 0 ? (
              <div className="em-dropdown-item">No results found</div>
            ) : (
              filtered.map((item) => (
                <label key={item.value} className="em-dropdown-item em-dropdown-item--checkbox" onClick={() => toggleValue(item.value)}>
                  <input
                    type="checkbox"
                    checked={values.includes(item.value)}
                    readOnly
                  />
                  <span>{item.label}</span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SearchableSelect({ items, value, onChange, placeholder, name }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  const filtered = items.filter(item =>
    item.label.toLowerCase().includes(search.toLowerCase()) ||
    item.value.toLowerCase().includes(search.toLowerCase())
  );

  const selectedItem = items.find(i => i.value === value);

  return (
    <div className="em-dropdown em-searchable-select" ref={ref}>
      <button type="button" className="em-input em-input--dropdown" onClick={() => setOpen(!open)}>
        <span className={selectedItem ? "em-multiselect-has-value" : "em-multiselect-placeholder"}>
          {selectedItem ? selectedItem.label : placeholder}
        </span>
        <ChevronDown size={14} className="em-multiselect-icon" />
      </button>
      {open && (
        <div className="em-dropdown-menu em-dropdown-menu--single">
          <div className="em-dropdown-search-wrap">
            <Search size={13} className="em-dropdown-search-icon" />
            <input
              className="em-dropdown-search-input"
              autoFocus
              placeholder="Search by name or ID..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              onClick={e => e.stopPropagation()}
            />
          </div>
          <div className="em-dropdown-options-list">
            {filtered.length === 0 ? (
              <div className="em-dropdown-item">No results found</div>
            ) : (
              filtered.map(item => (
                <div
                  key={item.value}
                  className={`em-dropdown-item ${item.value === value ? "active" : ""}`}
                  onClick={() => {
                    onChange({ target: { name, value: item.value } });
                    setOpen(false);
                    setSearch("");
                  }}
                >
                  {item.label}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Field wrapper ─────────────────────────────────────────────────────────────
function Field({ label, error, required, hint, children }) {
  return (
    <div className="em-field">
      {label && <label className="em-label">{label}{required && <span className="em-required"> *</span>}</label>}
      {children}
      {hint && <span className="em-field-hint">{hint}</span>}
      {error && <span className="em-field-error"><AlertCircle style={ICON_XS} />{error}</span>}
    </div>
  );
}

// ── Stat Cards ────────────────────────────────────────────────────────────────
function StatCards({ total = 0, active = 0, inactive = 0 }) {
  const stats = [
    { label: "Total Employees", value: total, Icon: Users, color: "#3b5bdb", bg: "#eef2ff" },
    { label: "Active", value: active, Icon: UserCheck, color: "#1a7f3c", bg: "#d3f9d8" },
    { label: "Inactive", value: inactive, Icon: UserMinus, color: "#5a6478", bg: "#f1f3f5" },
  ];
  return (
    <div className="em-stat-row">
      {stats.map(s => (
        <div key={s.label} className="em-stat-card" style={{ "--sc": s.color, "--sb": s.bg }}>
          <div className="em-stat-icon"><s.Icon size={20} /></div>
          <div><div className="em-stat-value">{s.value}</div><div className="em-stat-label">{s.label}</div></div>
        </div>
      ))}
    </div>
  );
}

// ── Employee Table ────────────────────────────────────────────────────────────
function EmployeeTable({ employees, loading, total, page, onPageChange, onView, onEdit, onDelete, onAdd, onFilterChange, filters, dropdowns, rowsPerPage, onRowsPerPageChange }) {
  const { search, department, designation, status, workMode, role } = filters;
  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));
  const hasFilters = search || department !== "all" || designation !== "all" || status !== "all" || workMode !== "all" || role !== "all";

  const clear = () => {
    onFilterChange({ search: "", department: "all", designation: "all", status: "all", workMode: "all", role: "all" });
    onPageChange(1);
  };

  const deptOptions = (dropdowns?.DEPARTMENT || []).length > 0 ? dropdowns.DEPARTMENT : DEPARTMENTS.map(d => ({ value: d, label: d }));

  return (
    <>
      <StatCards total={total} active={filters.overallActive || 0} inactive={filters.overallInactive || 0} />
      <div className="em-page-header" style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '20px', alignItems: 'stretch' }}>
        <div className="em-page-header__title" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#0f172a' }}>All Employees</h2>
          <span style={{ color: '#94a3b8' }}>-</span>
          <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>View and manage your organization's employee records</p>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
          {/* Search */}
          <div style={{ display: 'flex', alignItems: 'center', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '4px 10px', flex: '0 0 200px' }}>
            <Search size={14} color="#94a3b8" style={{ flexShrink: 0 }} />
            <input
              style={{ border: 'none', outline: 'none', padding: '2px 8px', width: '100%', fontSize: '13px', background: 'transparent' }}
              placeholder="Search..."
              value={search}
              onChange={e => { onFilterChange({ ...filters, search: e.target.value }); onPageChange(1); }}
            />
            {search && (
              <button onClick={() => { onFilterChange({ ...filters, search: "" }); onPageChange(1); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 0, display: 'flex' }}><X size={12} /></button>
            )}
          </div>

          {/* Filters */}
          <select style={{ fontSize: '13px', padding: '6px 10px', borderRadius: '6px', border: '1px solid #e2e8f0', outline: 'none', background: '#fff' }} value={department} onChange={e => { onFilterChange({ ...filters, department: e.target.value }); onPageChange(1); }}>
            <option value="all">Departments</option>
            {deptOptions.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
          <select style={{ fontSize: '13px', padding: '6px 10px', borderRadius: '6px', border: '1px solid #e2e8f0', outline: 'none', background: '#fff' }} value={designation || "all"} onChange={e => { onFilterChange({ ...filters, designation: e.target.value }); onPageChange(1); }}>
            <option value="all">Designations</option>
            {((dropdowns?.DESIGNATION || []).length > 0 ? dropdowns.DESIGNATION : DESIGNATIONS.map(d => ({ value: d, label: d }))).map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
          <select style={{ fontSize: '13px', padding: '6px 10px', borderRadius: '6px', border: '1px solid #e2e8f0', outline: 'none', background: '#fff' }} value={status} onChange={e => { onFilterChange({ ...filters, status: e.target.value }); onPageChange(1); }}>
            <option value="all">Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <select style={{ fontSize: '13px', padding: '6px 10px', borderRadius: '6px', border: '1px solid #e2e8f0', outline: 'none', background: '#fff' }} value={workMode} onChange={e => { onFilterChange({ ...filters, workMode: e.target.value }); onPageChange(1); }}>
            <option value="all">Modes</option>
            {(dropdowns?.WORK_MODE || []).length > 0
              ? (dropdowns.WORK_MODE).map(w => <option key={w.value} value={w.value}>{w.label}</option>)
              : <>
                <option value="office">Office</option>
                <option value="wfh">Remote</option>
                <option value="hybrid">Hybrid</option>
              </>
            }
          </select>
          <select style={{ fontSize: '13px', padding: '6px 10px', borderRadius: '6px', border: '1px solid #e2e8f0', outline: 'none', background: '#fff' }} value={role} onChange={e => { onFilterChange({ ...filters, role: e.target.value }); onPageChange(1); }}>
            <option value="all">Roles</option>
            {(dropdowns?.ROLE || []).map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          
          {hasFilters && <button style={{ background: '#fff', border: '1px solid #fee2e2', padding: '6px 10px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', color: '#dc2626', fontWeight: 500 }} onClick={clear}>Clear</button>}
          
          <div style={{ marginLeft: 'auto', display: 'flex' }}>
            {onAdd && <button style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#3b82f6', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: '6px', fontSize: '13px', fontWeight: '500', cursor: 'pointer', height: 'fit-content' }} onClick={onAdd}><Plus size={14} /> Add Employee</button>}
          </div>
        </div>
      </div>

      <div className="em-table-wrap">
        <table className="em-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th className="em-col-hide-sm">Department</th>
              <th className="em-col-hide-md">Designation</th>
              <th className="em-col-hide-md">Phone</th>
              <th>Status</th>
              <th className="em-col-hide-sm">Work Mode</th>
              <th style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7}>
                <div className="em-empty">
                  <RefreshCw size={28} style={{ opacity: 0.4, animation: "spin 1s linear infinite" }} />
                  <p>Loading employees...</p>
                </div>
              </td></tr>
            ) : employees.length === 0 ? (
              <tr><td colSpan={7}>
                <div className="em-empty">
                  <Filter size={36} style={{ opacity: 0.4 }} />
                  <p>No employees found</p>
                  <span>Try adjusting your search or filters</span>
                </div>
              </td></tr>
            ) : employees.map(emp => {
              const av = getAvatar(emp.employeeId);
              const sc = getStatusConfig(emp.employmentStatus);
              return (
                <tr key={emp.employeeId} className="em-table-row" onClick={() => onView(emp)}>
                  <td>
                    <div className="em-emp-cell">
                      {emp.profilePicUrl ? (
                        <img src={emp.profilePicUrl} alt={fullName(emp)} className="em-avatar em-avatar--img" onError={e => { e.target.style.display = "none"; }} />
                      ) : (
                        <div className="em-avatar" style={{ background: av.bg, color: av.color }}>{getInitials(emp)}</div>
                      )}
                      <div className="em-emp-info">
                        <span className="em-emp-name">{fullName(emp)}</span>
                        <span className="em-emp-sub">{emp.employeeId} · {emp.email}</span>
                      </div>
                    </div>
                  </td>
                  <td className="em-cell-muted em-col-hide-sm">
                    {resolveLabel(emp.department, dropdowns?.DEPARTMENT)}
                  </td>
                  <td className="em-cell-muted em-col-hide-md">
                    {resolveLabel(emp.designation, dropdowns?.DESIGNATION)}
                  </td>
                  <td className="em-col-hide-md">{emp.phoneNumber}</td>
                  <td><span className={`em-badge ${sc?.cls}`}>{sc?.label}</span></td>
                  <td className="em-col-hide-sm"><span className="em-badge em-badge--outline">{WORK_MODE_LABEL[emp.workMode] || emp.workMode}</span></td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }} onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                      <button className="em-icon-btn" onClick={() => onView(emp)} title="View Details">
                        <Eye size={15} />
                      </button>
                      {onEdit && (
                        <button className="em-icon-btn" onClick={() => onEdit(emp)} title="Edit">
                          <Pencil size={15} />
                        </button>
                      )}
                      {onDelete && (
                        <button className="em-icon-btn" style={{ color: '#ef4444' }} onClick={() => onDelete(emp)} title="Delete">
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {total > 0 && (
        <div className="em-pagination">
          <div className="em-pagination__left">
            <span className="em-pagination__info">
              Showing {Math.min((page - 1) * rowsPerPage + 1, total)}–{Math.min(page * rowsPerPage, total)} of {total}
            </span>
            <div className="em-rows-selector">
              <span className="em-rows-label">Rows:</span>
              <select
                className="em-rows-select"
                value={rowsPerPage}
                onChange={(e) => onRowsPerPageChange(Number(e.target.value))}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={30}>30</option>
                <option value={40}>40</option>
              </select>
            </div>
          </div>
          <div className="em-pagination__btns">
            <button className="em-page-btn" disabled={page === 1} onClick={() => onPageChange(page - 1)}><ChevronLeft size={15} /></button>
            {Array.from({ length: totalPages }, (_, i) => (
              <button key={i + 1} className={`em-page-btn${page === i + 1 ? " active" : ""}`} onClick={() => onPageChange(i + 1)}>{i + 1}</button>
            ))}
            <button className="em-page-btn" disabled={page === totalPages} onClick={() => onPageChange(page + 1)}><ChevronRight size={15} /></button>
          </div>
        </div>
      )}
    </>
  );
}

// ── Export CSV ────────────────────────────────────────────────────────────────
function exportEmployeeDocs(employee) {
  const rows = [["Document Type", "File Name", "File Size", "Uploaded On"]];
  (employee.documents || []).forEach(d => {
    rows.push([d.doc_label || d.docLabel || d.doc_type, d.file_name || d.fileName, d.file_size || "—", d.uploaded_on || "—"]);
  });
  const csv = rows.map(r => r.map(c => `"${c}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${employee.employeeId}_documents.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function EmployeeDetail({ employee: initialEmployee, onBack, onEdit, onDelete, token, dropdowns = {} }) {
  const [employee, setEmployee] = useState(() => normalizeEmployee(initialEmployee));
  const [loadingEmployee, setLoadingEmployee] = useState(true);
  const [docs, setDocs] = useState([]);
  const [wh, setWh] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [loadingWh, setLoadingWh] = useState(true);
  const [deletingDoc, setDeletingDoc] = useState(null);
  const [confirmDoc, setConfirmDoc] = useState(null);
  const [leadEmployee, setLeadEmployee] = useState(null);
  const [reportingHeadEmployee, setReportingHeadEmployee] = useState(null);
  const [hrEmployees, setHrEmployees] = useState([]);
  const [loadingLead, setLoadingLead] = useState(false);
  const [loadingReportingHead, setLoadingReportingHead] = useState(false);
  const [loadingHRs, setLoadingHRs] = useState(false);

  useEffect(() => {
    const empId = initialEmployee.employeeId || initialEmployee.employee_id;

    api.getEmployee(empId, token)
      .then(data => setEmployee(normalizeEmployee(data)))
      .catch(() => { })
      .finally(() => setLoadingEmployee(false));

    api.getDocuments(empId, token)
      .then(setDocs)
      .catch(() => toast.error("Failed to load documents"))
      .finally(() => setLoadingDocs(false));

    api.getWorkHistory(empId, token)
      .then(data => setWh(Array.isArray(data) ? data : []))
      .catch(() => toast.error("Failed to load work history"))
      .finally(() => setLoadingWh(false));
  }, [initialEmployee.employeeId, token]);

  useEffect(() => {
    if (!employee.leadEmployeeId) {
      setLeadEmployee(null);
    } else {
      setLoadingLead(true);
      api.getEmployee(employee.leadEmployeeId, token)
        .then(data => setLeadEmployee(normalizeEmployee(data)))
        .catch(() => setLeadEmployee(null))
        .finally(() => setLoadingLead(false));
    }

    if (!employee.reportingHeadEmployeeId) {
      setReportingHeadEmployee(null);
    } else {
      setLoadingReportingHead(true);
      api.getEmployee(employee.reportingHeadEmployeeId, token)
        .then(data => setReportingHeadEmployee(normalizeEmployee(data)))
        .catch(() => setReportingHeadEmployee(null))
        .finally(() => setLoadingReportingHead(false));
    }

    if (!employee.hrEmployeeIds || employee.hrEmployeeIds.length === 0) {
      setHrEmployees([]);
    } else {
      setLoadingHRs(true);
      Promise.all(employee.hrEmployeeIds.map(id => api.getEmployee(id, token)))
        .then(data => setHrEmployees(data.map(normalizeEmployee)))
        .catch(() => setHrEmployees([]))
        .finally(() => setLoadingHRs(false));
    }
  }, [employee.leadEmployeeId, employee.reportingHeadEmployeeId, employee.hrEmployeeIds, token]);

  const av = getAvatar(employee.employeeId);
  const sc = getStatusConfig(employee.employmentStatus);
  const education = employee.education || [];

  const handleDeleteDoc = async (docId) => {
    setDeletingDoc(docId);
    try {
      await api.deleteDocument(employee.employeeId, docId, token);
      setDocs(prev => prev.filter(d => d.id !== docId));
      toast.success("Document deleted");
    } catch (e) {
      toast.error(e.message || "Delete failed");
    } finally {
      setDeletingDoc(null);
      setConfirmDoc(null);
    }
  };

  // Total experience calculation
  const prevExpMonths = wh.reduce((s, e) => {
    if (!e.joining_date) return s;
    const endDate = (e.relieving_date && !e.currently_working)
      ? new Date(e.relieving_date)
      : new Date();
    const m = (endDate - new Date(e.joining_date)) / (1000 * 60 * 60 * 24 * 30.44);
    return s + Math.max(0, Math.round(m));
  }, 0);

  const curMonths = (() => {
    if (!employee.joiningDate) return 0;
    return Math.max(0, Math.round(
      (new Date() - new Date(employee.joiningDate)) / (1000 * 60 * 60 * 24 * 30.44)
    ));
  })();
  const totalMonths = curMonths + prevExpMonths;

  // ── BUG 1 FIX: Badge logic for WH entries ────────────────────────────────
  // same company + no relieving_date  → Current  (promotion, end date not set yet)
  // same company + has relieving_date → Previous Role (that role ended, moved to next)
  // different company                 → Previous always
  // const getWhBadge = (exp) => {
  //   const isSame   = exp.company_type === "same";
  //   const hasEnd   = !!(exp.relieving_date && exp.relieving_date !== "");
  //   const flagged  = exp.currently_working === true || exp.currently_working === 1;

  //   if (flagged || (isSame && !hasEnd)) {
  //     return { isCurrent: true, label: "Current" };
  //   }
  //   return {
  //     isCurrent: false,
  //     label: isSame ? "Previous Role" : "Previous",
  //   };
  // };

  //  EmployeeDetail getWhBadge function
  const getWhBadge = (exp) => {
    const hasEnd = !!(exp.relieving_date && exp.relieving_date !== "");

    if (!hasEnd) {
      return { isCurrent: true, label: "Current" };
    }
    return {
      isCurrent: false,
      label: exp.company_type === "same" ? "Previous Role" : "Previous",
    };
  };

  const sections = [
    {
      title: "Basic Info",
      icon: <User size={14} />,
      rows: [
        ["Full Name", fullName(employee)],
        ["Employee ID", employee.employeeId],
        ["Gender", (() => {
          const v = resolveLabel(employee.gender, dropdowns.GENDERS) || employee.gender;
          return v ? v.charAt(0).toUpperCase() + v.slice(1) : null;
        })()],
        ["Date of Birth", fmtDateFull(employee.dateOfBirth)],
        ["Blood Group", resolveLabel(employee.bloodGroup, dropdowns.BLOOD_GROUP) || employee.bloodGroup],
        ["Marital Status", (() => {
          const v = resolveLabel(employee.maritalStatus, dropdowns.MARITAL_STATUS) || employee.maritalStatus;
          return v ? v.charAt(0).toUpperCase() + v.slice(1) : null;
        })()],
        ["Employment Type", resolveLabel(employee.employmentType, dropdowns.EMPLOYEE_TYPE)
          || ETYPE_LABEL[employee.employmentType]
          || employee.employmentType],
      ]
    },
    {
      title: "Employment",
      icon: <Briefcase size={14} />,
      rows: [
        ["Department", resolveLabel(employee.department, dropdowns.DEPARTMENT) || employee.department],
        ["Designation", resolveLabel(employee.designation, dropdowns.DESIGNATION) || employee.designation],
        ["Role", resolveLabel(employee.role, dropdowns.ROLE) || employee.role],
        ["Joining Date", fmtDateFull(employee.joiningDate)],
        ["Probation End", fmtDateFull(employee.probationEndDate)],
        ...(employee.exitDate ? [["Exit Date", fmtDateFull(employee.exitDate)]] : []),
        ...(employee.exitReason ? [["Exit Reason", employee.exitReason]] : []),
        ...(employee.exitReason === "Others" && employee.exitRemarks ? [["Exit Remarks", employee.exitRemarks]] : []),
        ["Employment Status", getStatusConfig(employee.employmentStatus)?.label || employee.employmentStatus],
        ["Lead", leadEmployee ? `${fullName(leadEmployee)} (${leadEmployee.employeeId})` : (loadingLead ? "Loading..." : "—")],
        ["Reporting Head", reportingHeadEmployee ? `${fullName(reportingHeadEmployee)} (${reportingHeadEmployee.employeeId})` : (loadingReportingHead ? "Loading..." : "—")],
        ["HR", hrEmployees.length > 0 ? hrEmployees.map(hr => `${fullName(hr)} (${hr.employeeId})`).join(", ") : (loadingHRs ? "Loading..." : "—")],
      ]
    },
    {
      title: "Contact",
      icon: <Phone size={14} />,
      rows: [
        ["Phone", employee.phoneNumber],
        ["Alternate Phone", employee.alternatePhone],
        ["Personal Email", employee.email],
        ["Office Email", employee.officeEmail],
        ["Emergency Contact", employee.emergencyContactName],
        ["Emergency Phone", employee.emergencyContactNumber],
      ]
    },
    {
      title: "Address",
      icon: <MapPin size={14} />,
      rows: [
        ["Address", [employee.addressLine1, employee.addressLine2].filter(Boolean).join(", ")],
        ["City", employee.city],
        ["State", employee.state],
        ["Country", employee.country],
        ["Postal Code", employee.postalCode],
      ]
    },
    {
      title: "Work Details",
      icon: <Settings size={14} />,
      rows: [
        ["Work Location", employee.workLocation],
        ["Work Mode", resolveLabel(employee.workMode, dropdowns.WORK_MODE)
          || WORK_MODE_LABEL[employee.workMode]
          || employee.workMode],
        ["Shift Timing", resolveLabel(employee.shiftTiming, dropdowns.SHIFTS) || employee.shiftTiming],
        ["Time Zone", employee.timeZone],
        ["Business Unit", employee.businessUnit],
        ["Team", employee.team],
      ]
    },
    {
      title: "Statutory Details",
      icon: <FileText size={14} />,
      rows: [
        ["PAN Number", employee.panNumber],
        ["Aadhaar Number", employee.aadhaarNumber],
        ["ESI Number", employee.esiNumber],
        ["UAN Number", employee.uanNumber],
      ]
    },
    {
      title: "Bank Details",
      icon: <CreditCard size={14} />,
      rows: [
        ["Bank Name", employee.bankName],
        ["IFSC Code", employee.ifscCode],
        ["Account Number", employee.accountNumber],
        ["Branch", employee.branch],
      ]
    },
  ];

  // Build education list from educationHistory or fallback to old flat fields
  const rawEduHistory = employee?.educationHistory || [];

  return (
    <>
      <ConfirmModal
        isOpen={!!confirmDoc}
        title="Delete Document"
        message="Are you sure you want to delete this document? This cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={() => handleDeleteDoc(confirmDoc)}
        onCancel={() => setConfirmDoc(null)}
      />

      <div className="em-page-header">
        <button className="em-btn em-btn--outline em-btn--sm" onClick={onBack}>
          <ArrowLeft size={14} /> Back
        </button>
        <div style={{ display: "flex", gap: "10px", marginLeft: "auto" }}>
          {onEdit && (
            <button className="em-btn em-btn--primary em-btn--sm" onClick={() => onEdit(employee)}>
              <Pencil size={14} /> Edit Employee
            </button>
          )}
          {onDelete && (
            <button className="em-btn em-btn--outline em-btn--sm" style={{ color: "#ef4444", borderColor: "#ef4444" }} onClick={() => onDelete(employee)}>
              <Trash2 size={14} /> Delete Employee
            </button>
          )}
        </div>
      </div>

      {loadingEmployee ? (
        <div className="em-empty" style={{ padding: "48px 0" }}>
          <RefreshCw size={28} style={{ opacity: 0.4, animation: "spin 1s linear infinite" }} />
          <p>Loading employee details...</p>
        </div>
      ) : (
        <>
          {/* Profile Header */}
          <div className="em-profile-header">
            {employee.profilePicUrl ? (
              <img
                src={employee.profilePicUrl}
                alt={fullName(employee)}
                className="em-profile-avatar em-profile-avatar--img"
                onError={e => { e.target.style.display = "none"; }}
              />
            ) : (
              <div className="em-profile-avatar" style={{ background: av.bg, color: av.color }}>
                {getInitials(employee)}
              </div>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="em-profile-name">{fullName(employee)}</div>
              <div className="em-profile-sub">
                {[
                  resolveLabel(employee.designation, dropdowns.DESIGNATION) || employee.designation,
                  resolveLabel(employee.department, dropdowns.DEPARTMENT) || employee.department,
                  employee.workLocation,
                ].filter(Boolean).join(" · ")}
              </div>
              <div className="em-profile-badges">
                <span className={`em-badge ${sc?.cls}`}>{sc?.label}</span>
                <span className="em-badge em-badge--outline">
                  {resolveLabel(employee.workMode, dropdowns.WORK_MODE)
                    || WORK_MODE_LABEL[employee.workMode]
                    || employee.workMode}
                </span>
                <span className="em-badge em-badge--outline">{employee.employeeId}</span>
                {employee.bloodGroup && (
                  <span className="em-badge em-badge--outline" style={{ color: "#dc2626", borderColor: "#fecaca" }}>
                    <Droplets size={11} style={{ marginRight: 3 }} />
                    {resolveLabel(employee.bloodGroup, dropdowns.BLOOD_GROUP) || employee.bloodGroup}
                  </span>
                )}
              </div>
            </div>
            <div className="em-exp-chip">
              <Clock size={20} />
              <div>
                <div className="em-exp-chip__val">{monthsToStr(totalMonths)}</div>
                <div className="em-exp-chip__lbl">Total Experience</div>
              </div>
            </div>
          </div>

          {/* Detail Grid */}
          <div className="em-detail-grid">
            {sections.map(sec => (
              <div key={sec.title} className="em-detail-card">
                <div className="em-detail-card__title" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  {sec.icon} {sec.title}
                </div>
                {sec.rows.map(([label, value]) =>
                  value ? (
                    <div key={label} className="em-detail-row">
                      <span className="em-detail-label">{label}</span>
                      <span className="em-detail-value">{value}</span>
                    </div>
                  ) : null
                )}
              </div>
            ))}

            {/* Education list cards */}
            {rawEduHistory.length > 0 && rawEduHistory.map((edu, idx) => (
              <div key={`edu-${idx}`} className="em-detail-card">
                <div className="em-detail-card__title" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <GraduationCap size={14} /> Education {rawEduHistory.length > 1 ? `#${idx + 1}` : ""}
                </div>
                {edu.qualification && (
                  <div className="em-detail-row">
                    <span className="em-detail-label">Qualification</span>
                    <span className="em-detail-value">{edu.qualification}</span>
                  </div>
                )}
                {edu.courseName && (
                  <div className="em-detail-row">
                    <span className="em-detail-label" style={{ display: "flex", alignItems: "center", gap: 4 }}><BookOpen size={12} /> Course</span>
                    <span className="em-detail-value">{edu.courseName}</span>
                  </div>
                )}
                {edu.institute && (
                  <div className="em-detail-row">
                    <span className="em-detail-label">Institute</span>
                    <span className="em-detail-value">{edu.institute}</span>
                  </div>
                )}
                {edu.percentage && (
                  <div className="em-detail-row">
                    <span className="em-detail-label">Percentage</span>
                    <span className="em-detail-value">{edu.percentage}</span>
                  </div>
                )}
              </div>
            ))}

            {String(employee.employmentStatus).toLowerCase() === "inactive" && employee.statusReason && (
              <div className="em-detail-card">
                <div className="em-detail-card__title">Inactive Reason</div>
                <div className="em-detail-row">
                  <span
                    className="em-detail-value"
                    style={{
                      textAlign: "left", color: "#92400e", background: "#fffbeb",
                      padding: "8px 10px", borderRadius: 6, width: "100%",
                      display: "block", fontSize: 13,
                    }}
                  >
                    {employee.statusReason}
                  </span>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* ── Documents ── */}
      <div className="em-detail-card em-detail-card--full">
        <div className="em-detail-card__title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <FileText size={14} /> Documents
          <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontWeight: 400, fontSize: 12, color: "var(--em-text-3)", textTransform: "none", letterSpacing: 0 }}>
              {docs.length} file{docs.length !== 1 ? "s" : ""}
            </span>
            {docs.length > 0 && (
              <button
                className="em-btn em-btn--outline em-btn--sm"
                style={{ textTransform: "none", letterSpacing: 0, gap: 5 }}
                onClick={() => exportEmployeeDocs({ ...employee, documents: docs })}
              >
                <FileSpreadsheet size={13} /> Export CSV
              </button>
            )}
          </span>
        </div>

        {loadingDocs ? (
          <div className="em-empty" style={{ padding: "24px 0" }}>
            <RefreshCw size={20} style={{ opacity: 0.4 }} />
            <p>Loading documents...</p>
          </div>
        ) : docs.length === 0 ? (
          <div className="em-empty" style={{ padding: "32px 0" }}>
            <FileText size={28} style={{ opacity: 0.3 }} />
            <p style={{ fontSize: 14 }}>No documents uploaded</p>
          </div>
        ) : (
          <div className="em-doc-view-grid">
            {docs.map(doc => (
              <div key={doc.id} className="em-doc-view-card">
                <div className="em-doc-view-icon"><FileText size={20} /></div>
                <div className="em-doc-view-info">
                  <div className="em-doc-view-type">{doc.doc_label || doc.docLabel}</div>
                  <div className="em-doc-view-name">{doc.file_name || doc.fileName}</div>
                  <div className="em-doc-view-meta">
                    {doc.file_size || "—"} · {doc.uploaded_on
                      ? (() => {
                        const dt = new Date(doc.uploaded_on);
                        if (isNaN(dt.getTime())) return "—";
                        const local = new Date(dt.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
                        return `${String(local.getDate()).padStart(2, '0')}-${String(local.getMonth() + 1).padStart(2, '0')}-${local.getFullYear()}`;
                      })()
                      : "—"}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  {doc.signed_url && (
                    <a href={doc.signed_url} target="_blank" rel="noreferrer"
                      className="em-doc-view-dl" title="Download">
                      <Download size={15} />
                    </a>
                  )}
                  <button
                    className="em-doc-view-dl"
                    style={{ color: "#ef4444" }}
                    onClick={() => setConfirmDoc(doc.id)}
                    disabled={deletingDoc === doc.id}
                    title="Delete"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Work History ── */}
      <div className="em-detail-card em-detail-card--full">
        <div className="em-detail-card__title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <History size={14} /> Work History
          <span style={{ marginLeft: "auto", fontWeight: 400, fontSize: 12, color: "var(--em-text-3)", textTransform: "none", letterSpacing: 0 }}>
            {wh.length + 1} records · Total exp: {monthsToStr(totalMonths)}
          </span>
        </div>

        {loadingWh ? (
          <div className="em-empty" style={{ padding: "24px 0" }}>
            <RefreshCw size={20} style={{ opacity: 0.4 }} />
            <p>Loading work history...</p>
          </div>
        ) : (
          <div className="em-wh-timeline">

            {/* ── Main employee entry — ALWAYS "Current" ── */}
            <div className="em-wh-item">
              <div className="em-wh-dot em-wh-dot--current" />
              {wh.length > 0 && <div className="em-wh-line" />}
              <div className="em-wh-body">
                <div className="em-wh-top">
                  <div>
                    {/* BUG 2 FIX: resolve role label */}
                    <div className="em-wh-title">
                      {resolveLabel(employee.role, dropdowns.ROLE) || employee.role || "—"}
                    </div>
                    {employee.designation && (
                      <div className="em-wh-designation">
                        {/* BUG 2 FIX: resolve designation name */}
                        {resolveLabel(employee.designation, dropdowns.DESIGNATION) || employee.designation}
                      </div>
                    )}
                    <div className="em-wh-company">{employee.workLocation || "—"}</div>
                    {employee.department && (
                      <div className="em-wh-sub" style={{ marginTop: 2 }}>
                        {/* BUG 2 FIX: resolve department name */}
                        <span>{resolveLabel(employee.department, dropdowns.DEPARTMENT) || employee.department}</span>
                        {employee.team && (
                          <><span className="em-wh-sep">·</span><span>{employee.team}</span></>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="em-wh-meta">
                    {employee.employmentType && (
                      <span className="em-badge em-badge--outline">
                        {resolveLabel(employee.employmentType, dropdowns.EMPLOYEE_TYPE)
                          || ETYPE_LABEL[employee.employmentType]
                          || employee.employmentType}
                      </span>
                    )}
                    {/* BUG 1 FIX: Main entry always Current — no condition */}
                    <span className="em-badge em-badge--active" style={{ fontSize: 10.5 }}>Current</span>
                    <div style={{ textAlign: "right" }}>
                      {/* BUG 3 FIX: fmtDateFull → "06 Apr 2026" format */}
                      <div className="em-wh-dates">
                        {fmtDateFull(employee.joiningDate) || "?"} – Present
                      </div>
                      <div className="em-wh-duration">
                        {calcDuration(employee.joiningDate, null)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Previous WH entries ── */}
            {[...wh].reverse().map((exp, idx) => {
              // BUG 1 FIX: Smart badge logic
              // same company + no relieving_date  → Current  (promotion, end not set)
              // same company + has relieving_date → Previous Role (that role ended)
              // different company                 → Previous
              const { isCurrent, label: badgeLabel } = getWhBadge(exp);

              return (
                <div key={exp.id ?? idx} className="em-wh-item">
                  <div className="em-wh-dot" />
                  {idx < wh.length - 1 && <div className="em-wh-line" />}
                  <div className="em-wh-body">
                    <div className="em-wh-top">
                      <div>
                        {/* BUG 2 FIX: resolve role label for WH entries */}
                        <div className="em-wh-title">
                          {resolveLabel(exp.role, dropdowns.ROLE) || exp.role || "—"}
                        </div>
                        {exp.designation && (
                          <div className="em-wh-designation">
                            {/* BUG 2 FIX: resolve designation for WH entries */}
                            {resolveLabel(exp.designation, dropdowns.DESIGNATION) || exp.designation}
                          </div>
                        )}
                        <div className="em-wh-company">
                          {exp.company_type === "same"
                            ? "(Current Company)"
                            : (exp.company_name || "—")}
                        </div>
                        {(exp.department || exp.company_address) && (
                          <div className="em-wh-sub" style={{ marginTop: 2 }}>
                            {exp.department && (
                              <span>
                                {/* BUG 2 FIX: resolve department for WH entries */}
                                {resolveLabel(exp.department, dropdowns.DEPARTMENT) || exp.department}
                              </span>
                            )}
                            {exp.department && exp.company_address && (
                              <span className="em-wh-sep">·</span>
                            )}
                            {exp.company_type !== "same" && exp.company_address && (
                              <span>{exp.company_address}</span>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="em-wh-meta">
                        {exp.employment_type && (
                          <span className="em-badge em-badge--outline">
                            {resolveLabel(exp.employment_type, dropdowns.EMPLOYEE_TYPE)
                              || ETYPE_LABEL[exp.employment_type]
                              || exp.employment_type}
                          </span>
                        )}

                        {/* BUG 1 FIX: badge based on getWhBadge() result */}
                        {isCurrent ? (
                          <span className="em-badge em-badge--active" style={{ fontSize: 10.5 }}>
                            {badgeLabel}
                          </span>
                        ) : (
                          <span className="em-badge em-badge--outline" style={{ fontSize: 10.5 }}>
                            {badgeLabel}
                          </span>
                        )}

                        <div style={{ textAlign: "right" }}>
                          {/* BUG 3 FIX: fmtDateFull for both dates → "06 Apr 2026" */}
                          <div className="em-wh-dates">
                            {fmtDateFull(exp.joining_date) || "?"} –{" "}
                            {isCurrent ? "Present" : (fmtDateFull(exp.relieving_date) || "?")}
                          </div>
                          <div className="em-wh-duration">
                            {calcDuration(exp.joining_date, isCurrent ? null : exp.relieving_date)}
                          </div>
                        </div>
                      </div>
                    </div>

                    {exp.responsibilities && (
                      <div className="em-wh-resp">{exp.responsibilities}</div>
                    )}
                    {exp.reference_email && (
                      <div className="em-wh-ref">
                        <Mail style={ICON_XS} /> Ref:{" "}
                        <a href={`mailto:${exp.reference_email}`}>{exp.reference_email}</a>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

          </div>
        )}
      </div>
    </>
  );
}

// ── Profile Picture Upload ────────────────────────────────────────────────────
function ProfilePicUpload({ empId, currentUrl, token, onUpdate }) {
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(currentUrl || "");
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileRef = useRef(null);

  const processFile = async (file) => {
    if (!file) return;
    // Basic validation
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size should be less than 5MB");
      return;
    }

    const reader = new FileReader();
    reader.onload = ev => setPreviewUrl(ev.target.result);
    reader.readAsDataURL(file);

    if (!empId) {
      onUpdate({ file, previewUrl: URL.createObjectURL(file) });
      return;
    }

    setUploading(true);
    try {
      const res = await api.uploadProfilePic(empId, file, token);
      const url = res?.profile_pic_url || res?.url || previewUrl;
      setPreviewUrl(url);
      onUpdate({ file: null, previewUrl: url });
      toast.success("Profile picture updated");
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    processFile(file);
    e.target.value = "";
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (uploading) return;
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (uploading) return;
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  const handleRemove = async () => {
    setConfirmRemove(false);
    if (!empId) {
      setPreviewUrl("");
      onUpdate({ file: null, previewUrl: "" });
      return;
    }
    setUploading(true);
    try {
      await api.deleteProfilePic(empId, token);
      setPreviewUrl("");
      onUpdate({ file: null, previewUrl: "" });
      toast.success("Profile picture removed");
    } catch (err) {
      toast.error(err.message || "Remove failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <ConfirmModal
        isOpen={confirmRemove}
        title="Remove Profile Picture"
        message="Are you sure you want to remove the profile picture?"
        confirmLabel="Remove"
        cancelLabel="Cancel"
        onConfirm={handleRemove}
        onCancel={() => setConfirmRemove(false)}
      />
      <div className={`em-profile-pic-wrap${dragActive ? " is-dragging" : ""}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <div className="em-profile-pic-container">
          {previewUrl ? (
            <img src={previewUrl} alt="Profile" className="em-profile-pic-img" />
          ) : (
            <div className="em-profile-pic-placeholder"><User size={24} strokeWidth={1.5} /></div>
          )}
          {uploading && <div className="em-profile-pic-overlay"><RefreshCw size={18} style={{ animation: "spin 1s linear infinite" }} /></div>}
          <label className="em-profile-pic-camera" title="Change photo">
            <Camera size={13} />
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: "none" }} onChange={handleFileChange} disabled={uploading} />
          </label>
        </div>
        <div className="em-profile-pic-actions">
          <label className={`em-btn em-btn--outline em-btn--sm${uploading ? " disabled" : ""}`} style={{ cursor: uploading ? "not-allowed" : "pointer" }}>
            <Upload size={13} /> {previewUrl ? "Change Photo" : "Upload Photo"}
            <input type="file" accept="image/jpeg,image/png,image/webp" style={{ display: "none" }} onChange={handleFileChange} disabled={uploading} />
          </label>
          {previewUrl && (
            <button type="button" className="em-btn em-btn--ghost em-btn--sm" style={{ color: "#ef4444" }} onClick={() => setConfirmRemove(true)} disabled={uploading}>
              <Trash2 size={13} /> Remove
            </button>
          )}
          <span className="em-field-hint" style={{ marginTop: 4 }}>JPG, PNG or WebP · Drag & drop supported</span>
        </div>
      </div>
    </>
  );
}

// ── Documents Upload Section ──────────────────────────────────────────────────
function DocumentsSection({ documents, onChange }) {
  const [docType, setDocType] = useState("");

  const handleFiles = (files) => {
    if (!docType) { alert("Please select a document type first."); return; }
    const label = DOC_TYPES.find(d => d.value === docType)?.label || docType;
    Array.from(files).forEach(file => {
      const isImg = file.type.startsWith("image/");
      const newDoc = {
        id: `doc_${Date.now()}_${Math.random()}`,
        docType, docLabel: label,
        fileName: file.name,
        fileSize: file.size > 1024 * 1024 ? `${(file.size / (1024 * 1024)).toFixed(1)} MB` : `${Math.round(file.size / 1024)} KB`,
        uploadedOn: new Date().toISOString().split("T")[0],
        file,
        previewUrl: isImg ? URL.createObjectURL(file) : null,
        isPdf: file.type === "application/pdf"
      };
      onChange(prev => [...prev, newDoc]);
    });
    setDocType("");
  };

  const remove = (id) => {
    const docToRemove = documents.find(d => d.id === id);
    if (docToRemove?.previewUrl) URL.revokeObjectURL(docToRemove.previewUrl);
    onChange(prev => prev.filter(d => d.id !== id));
  };

  const grouped = DOC_TYPES.map(dt => ({ ...dt, docs: documents.filter(d => d.docType === dt.value) })).filter(g => g.docs.length > 0);

  return (
    <div className="em-form-card">
      <div className="em-form-card__title"><FileText size={15} /> Documents</div>
      <div className="em-doc-upload-row">
        <div className="em-doc-type-wrap">
          <select className="em-select-field" value={docType} onChange={e => setDocType(e.target.value)}>
            <option value="">Select document type...</option>
            {DOC_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
        </div>
        <label className={`em-doc-upload-btn${!docType ? " em-doc-upload-btn--disabled" : ""}`}>
          <Upload size={14} /><span>Choose Files</span>
          <input type="file" multiple accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" style={{ display: "none" }} disabled={!docType} onChange={e => handleFiles(e.target.files)} />
        </label>
        <span className="em-doc-hint">Select type → then choose files</span>
      </div>
      {documents.length === 0 && (
        <div className="em-we-empty" style={{ padding: "24px 0" }}>
          <Paperclip size={28} style={{ opacity: 0.3 }} />
          <p style={{ fontSize: 14 }}>No documents added yet</p>
        </div>
      )}
      {documents.length > 0 && (
        <div className="em-doc-section-list">
          <div className="em-doc-card-grid">
            {documents.map(doc => (
              <div key={doc.id} className="em-doc-card">
                <div className="em-doc-card-preview">
                  {doc.previewUrl ? (
                    <img src={doc.previewUrl} alt={doc.fileName} />
                  ) : (
                    <div className={`em-doc-placeholder ${doc.isPdf ? 'is-pdf' : ''}`}>
                      {doc.isPdf ? "PDF" : <FileText size={20} />}
                    </div>
                  )}
                  {/* Document Type Badge */}
                  <div className="em-doc-type-badge">{doc.docLabel}</div>
                </div>
                <div className="em-doc-card-info">
                  <span className="em-doc-card-name" title={doc.fileName}>{doc.fileName}</span>
                  <div className="em-doc-card-meta">
                    <span style={{ fontSize: '10.5px', color: '#4f46e5', fontWeight: 600 }}>
                      {doc.fileSize || "—"} · {fmtDateTime(doc.uploadedOn || doc.uploaded_on || doc.created_at)}
                    </span>
                  </div>
                </div>
                <button type="button" className="em-doc-card-remove" onClick={() => remove(doc.id)} title="Remove">
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Work Experience Section ───────────────────────────────────────────────────
const newWExp = () => ({
  id: `wh_${Date.now()}_${Math.random()}`,
  companyType: "different", companyName: "", companyAddress: "", referenceEmail: "",
  role: "", designation: "", department: "", employmentType: "",
  joiningDate: "", relievingDate: "", currentlyWorking: false,
  responsibilities: "", documents: [], whDocType: "", isExpanded: true,
});

function WorkExperienceSection({ experiences, onChange, dropdowns, onProbationChange }) {
  const add = () => onChange([...experiences, newWExp()]);
  const remove = (id) => onChange(experiences.filter(e => e.id !== id));
  const upd = (id, field, value) => onChange(experiences.map(e => e.id === id ? { ...e, [field]: value } : e));
  const toggle = (id) => onChange(experiences.map(e => e.id === id ? { ...e, isExpanded: !e.isExpanded } : e));

  // Probation auto-logic: same company relieving date → probation = relieving - 1 day
  const handleRelievingDateChange = (id, value) => {
    upd(id, "relievingDate", value);
    const exp = experiences.find(e => e.id === id);
    if (exp?.companyType === "same" && value && onProbationChange) {
      const probDate = oneDayBefore(value);
      onProbationChange(probDate);
    }
  };

  // FIX 3: When switching to "same company", auto-fill joiningDate = previous entry's relievingDate + 1 day
  const handleCompanyTypeChange = (id, newType) => {
    const idx = experiences.findIndex(e => e.id === id);
    let autoJoin = "";

    if (newType === "same" && idx > 0) {
      const prevEntry = experiences[idx - 1];
      if (prevEntry?.relievingDate) {
        autoJoin = oneDayAfter(prevEntry.relievingDate);
      }
    }

    onChange(
      experiences.map(e =>
        e.id === id
          ? {
            ...e,
            companyType: newType,
            // Only override joiningDate if we have an auto value and field is empty
            joiningDate: newType === "same" && autoJoin ? autoJoin : e.joiningDate,
            // Clear company fields when switching to same
            ...(newType === "same" ? { companyName: "", companyAddress: "", referenceEmail: "" } : {}),
          }
          : e
      )
    );
  };

  const addWhDocs = (expId, files) => {
    const exp = experiences.find(e => e.id === expId);
    if (!exp?.whDocType) return;
    const label = DOC_TYPES.find(d => d.value === exp.whDocType)?.label || exp.whDocType;
    Array.from(files).forEach(file => {
      const newDoc = {
        id: `whdoc_${Date.now()}_${Math.random()}`,
        docType: exp.whDocType, docLabel: label,
        fileName: file.name,
        fileSize: file.size > 1024 * 1024 ? `${(file.size / (1024 * 1024)).toFixed(1)} MB` : `${Math.round(file.size / 1024)} KB`,
        uploadedOn: new Date().toISOString().split("T")[0],
        file,
      };
      onChange(prev => prev.map(e => e.id === expId ? { ...e, documents: [...(e.documents || []), newDoc], whDocType: "" } : e));
    });
  };

  const removeWhDoc = (expId, docId) =>
    onChange(experiences.map(e => e.id === expId ? { ...e, documents: e.documents.filter(d => d.id !== docId) } : e));

  const roleOptions = dropdowns?.ROLE || [];
  const empTypeOptions = dropdowns?.EMPLOYEE_TYPE || [];
  const designationOptions = dropdowns?.DESIGNATION || [];
  const departmentOptions = dropdowns?.DEPARTMENT || [];

  return (
    <div className="em-form-card">
      <div className="em-form-card__title" style={{ justifyContent: "space-between" }}>
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}><History size={15} /> Work History</span>
        <button type="button" className="em-btn em-btn--primary em-btn--sm" onClick={add}><Plus size={14} /> Add Work History</button>
      </div>
      <div className="em-wh-info-tip">
        <AlertCircle size={14} />
        <span>Add all previous roles — including promotions within the same company.</span>
      </div>
      {experiences.length === 0 ? (
        <div className="em-we-empty">
          <History size={36} style={{ opacity: 0.35 }} />
          <p>No work history added</p>
          <button type="button" className="em-btn em-btn--primary em-btn--sm" onClick={add} style={{ marginTop: 4 }}><Plus size={14} /> Add Work History</button>
        </div>
      ) : (
        <div className="em-we-list">
          {experiences.map((exp, idx) => (
            <div key={exp.id} className={`em-we-card ${exp.isExpanded ? "em-we-card--open" : ""}`}>
              <div className="em-we-card__header" onClick={() => toggle(exp.id)}>
                <div className="em-we-card__header-left">
                  <div className={`em-we-card__dot ${exp.companyType === "same" ? "em-we-card__dot--same" : ""}`} />
                  <div style={{ minWidth: 0 }}>
                    <span className="em-we-card__num">Entry #{idx + 1}</span>
                    {(exp.role || exp.companyName) && (
                      <span className="em-we-card__preview">
                        {exp.role && <strong>{exp.role}</strong>}
                        {exp.role && exp.companyName && " · "}
                        {exp.companyName}
                        {exp.joiningDate && (
                          <span className="em-we-card__preview-date">
                            {" "}{fmtDate(exp.joiningDate)} – {exp.currentlyWorking ? "Present" : (fmtDate(exp.relievingDate) || "?")}
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                </div>
                <div className="em-we-card__header-right" onClick={e => e.stopPropagation()}>
                  <button type="button" className="em-we-collapse-btn" onClick={() => toggle(exp.id)}>
                    <ChevronDown size={14} style={{ transform: exp.isExpanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 200ms" }} />
                  </button>
                  <button type="button" className="em-we-remove" onClick={() => remove(exp.id)}><Trash2 size={14} /></button>
                </div>
              </div>

              {exp.isExpanded && (
                <div className="em-we-body">
                  {/* Company Type */}
                  <div className="em-we-section-label">Company Type</div>
                  <div className="em-we-type-toggle">
                    {/* FIX 3: Use handleCompanyTypeChange instead of direct upd */}
                    <button
                      type="button"
                      className={`em-we-type-btn ${exp.companyType === "same" ? "active" : ""}`}
                      onClick={() => handleCompanyTypeChange(exp.id, "same")}
                    >
                      <Building size={14} /> Same Company <span className="em-we-type-hint">Promotion / role change</span>
                    </button>
                    <button
                      type="button"
                      className={`em-we-type-btn ${exp.companyType === "different" ? "active" : ""}`}
                      onClick={() => handleCompanyTypeChange(exp.id, "different")}
                    >
                      <Briefcase size={14} /> Different Company <span className="em-we-type-hint">Previous employer</span>
                    </button>
                  </div>

                  {/* Company Info — only for different */}
                  {exp.companyType === "different" && (
                    <>
                      <div className="em-we-section-label" style={{ marginTop: 14 }}>Company Information</div>
                      <div className="em-grid-2">
                        <Field label="Company Name" required>
                          <input className="em-input" placeholder="e.g., Infosys Ltd" value={exp.companyName} onChange={e => upd(exp.id, "companyName", e.target.value)} />
                        </Field>
                        <Field label="Reference Email">
                          <div className="em-input-icon-wrap"><Mail style={ICON_SM} />
                            <input className="em-input em-input--icon" type="email" placeholder="hr@company.com" value={exp.referenceEmail} onChange={e => upd(exp.id, "referenceEmail", e.target.value)} />
                          </div>
                        </Field>
                      </div>
                      <Field label="Company Address">
                        <div className="em-input-icon-wrap"><MapPin style={ICON_SM} />
                          <input className="em-input em-input--icon" placeholder="City, State, Country" value={exp.companyAddress} onChange={e => upd(exp.id, "companyAddress", e.target.value)} />
                        </div>
                      </Field>
                    </>
                  )}

                  {exp.companyType === "same" && (
                    <div className="em-wh-same-notice">
                      <CheckCircle2 size={14} />
                      <span>
                        Same-company role (promotion/role change).
                        {exp.joiningDate && (
                          <> Joining date auto-set to <strong>{fmtDateFull(exp.joiningDate)}</strong> (day after previous role ended).</>
                        )}
                        {" "}Probation end will be set to 1 day before this role's end date.
                      </span>
                    </div>
                  )}

                  {/* Role Information */}
                  <div className="em-we-section-label" style={{ marginTop: 14 }}>Role Information</div>
                  <div className="em-grid-2">
                    <Field label="Role / Title" required>
                      <SearchableSelect
                        placeholder="Select role..."
                        value={exp.role}
                        items={roleOptions.length > 0 ? roleOptions : DESIGNATIONS.map(d => ({ value: d, label: d }))}
                        onChange={({ target }) => upd(exp.id, "role", target.value)}
                      />
                    </Field>
                    <Field label="Designation">
                      {exp.companyType === "different" ? (
                        <input
                          className="em-input"
                          placeholder="e.g., Software Engineer"
                          value={exp.designation || ""}
                          onChange={(e) => upd(exp.id, "designation", e.target.value)}
                        />
                      ) : (
                        <SearchableSelect
                          placeholder="Select designation..."
                          value={exp.designation}
                          items={designationOptions.length > 0 ? designationOptions : DESIGNATIONS.map(d => ({ value: d, label: d }))}
                          onChange={({ target }) => upd(exp.id, "designation", target.value)}
                        />
                      )}
                    </Field>
                  </div>
                  <div className="em-grid-2">
                    <Field label="Department">
                      {exp.companyType === "different" ? (
                        <input
                          className="em-input"
                          placeholder="e.g., Engineering"
                          value={exp.department || ""}
                          onChange={(e) => upd(exp.id, "department", e.target.value)}
                        />
                      ) : (
                        <SearchableSelect
                          placeholder="Select department..."
                          value={exp.department}
                          items={departmentOptions.length > 0 ? departmentOptions : DEPARTMENTS.map(d => ({ value: d, label: d }))}
                          onChange={({ target }) => upd(exp.id, "department", target.value)}
                        />
                      )}
                    </Field>
                    <Field label="Employment Type">
                      <SearchableSelect
                        placeholder="Select type..."
                        value={exp.employmentType}
                        items={empTypeOptions.length > 0 ? empTypeOptions : [
                          { value: "full-time", label: "Full Time" },
                          { value: "part-time", label: "Part Time" },
                          { value: "contract", label: "Contract" },
                          { value: "intern", label: "Internship" },
                          { value: "freelance", label: "Freelance" }
                        ]}
                        onChange={({ target }) => upd(exp.id, "employmentType", target.value)}
                      />
                    </Field>
                  </div>

                  {/* Duration */}
                  <div className="em-we-section-label" style={{ marginTop: 14 }}>Duration</div>
                  <div className="em-grid-2">
                    <Field label="Start Date" required>
                      <input
                        className="em-input"
                        type="date"
                        value={exp.joiningDate}
                        onChange={e => upd(exp.id, "joiningDate", e.target.value)}
                      />
                    </Field>
                    <Field label="End Date">
                      <input
                        className="em-input"
                        type="date"
                        value={exp.relievingDate}
                        disabled={exp.currentlyWorking}
                        style={exp.currentlyWorking ? { opacity: 0.45, cursor: "not-allowed" } : {}}
                        onChange={e => handleRelievingDateChange(exp.id, e.target.value)}
                      />
                      <label className="em-we-current-check">
                        <input
                          type="checkbox"
                          checked={exp.currentlyWorking}
                          onChange={e => {
                            upd(exp.id, "currentlyWorking", e.target.checked);
                            // FIX 2: if checking "currently working", clear relievingDate
                            if (e.target.checked) {
                              upd(exp.id, "relievingDate", "");
                            }
                          }}
                        />
                        Currently in this role
                      </label>
                    </Field>
                  </div>

                  {exp.joiningDate && (
                    <div className="em-duration-chip">
                      <Clock size={13} />Duration: <strong>
                        {calcDuration(
                          exp.joiningDate,
                          // FIX 2: null relievingDate OR currentlyWorking → treat as present
                          (exp.currentlyWorking || !exp.relievingDate) ? null : exp.relievingDate
                        )}
                      </strong>
                    </div>
                  )}

                  {/* Auto probation hint for same company */}
                  {exp.companyType === "same" && exp.relievingDate && (
                    <div className="em-wh-same-notice" style={{ marginTop: 0, background: "#f0fdf4", borderColor: "#bbf7d0" }}>
                      <CheckCircle2 size={14} style={{ color: "#16a34a" }} />
                      <span style={{ color: "#15803d" }}>
                        Probation end date auto-set to{" "}
                        <strong>{fmtDateFull(oneDayBefore(exp.relievingDate))}</strong>
                        {" "}(1 day before end date)
                      </span>
                    </div>
                  )}

                  <Field label="Key Responsibilities">
                    <textarea className="em-textarea" rows={3} placeholder="Describe roles and responsibilities..." value={exp.responsibilities} onChange={e => upd(exp.id, "responsibilities", e.target.value)} />
                  </Field>

                  {/* Supporting Docs — only for different company */}
                  {exp.companyType === "different" && (
                    <>
                      <div className="em-we-section-label" style={{ marginTop: 4 }}>Supporting Documents</div>
                      <div className="em-wh-doc-upload-row">
                        <select className="em-select-field" style={{ flex: 1, minWidth: 0 }} value={exp.whDocType || ""} onChange={e => upd(exp.id, "whDocType", e.target.value)}>
                          <option value="">Select document type...</option>
                          {DOC_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                        </select>
                        <label className={`em-doc-upload-btn${!exp.whDocType ? " em-doc-upload-btn--disabled" : ""}`}>
                          <Upload size={14} /><span>Choose Files</span>
                          <input type="file" multiple accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" style={{ display: "none" }} disabled={!exp.whDocType} onChange={e => addWhDocs(exp.id, e.target.files)} />
                        </label>
                      </div>
                      {(exp.documents || []).length > 0 ? (
                        <div className="em-wh-doc-list">
                          {exp.documents.map(doc => (
                            <div key={doc.id} className="em-doc-item em-doc-item--upload">
                              <div className="em-doc-item__icon"><FileText size={14} /></div>
                              <div className="em-doc-item__info">
                                <span className="em-doc-item__name">{doc.fileName}</span>
                                <span className="em-doc-item__meta"><span className="em-wh-doc-type-tag">{doc.docLabel}</span> · {doc.fileSize} · {fmtDateTime(doc.uploadedOn || doc.uploaded_on || doc.created_at)}</span>
                              </div>
                              <button className="em-doc-remove" onClick={() => removeWhDoc(exp.id, doc.id)}><X size={12} /></button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="em-wh-doc-empty"><Paperclip size={14} /><span>No documents attached</span></div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
          <button type="button" className="em-we-add-more" onClick={add}><Plus size={14} /> Add Another Work History Entry</button>
        </div>
      )}
    </div>
  );
}

// ── Employee Form ─────────────────────────────────────────────────────────────
const FORM_TABS = [
  { id: "basic", label: "Basic Info", Icon: User },
  { id: "employment", label: "Employment", Icon: Briefcase },
  { id: "education", label: "Education", Icon: GraduationCap },
  { id: "bank", label: "Bank Details", Icon: CreditCard },
  { id: "contact", label: "Contact", Icon: Phone },
  { id: "documents", label: "Documents", Icon: FileText },
  { id: "work", label: "Work Details", Icon: Settings },
];

function buildFormState(employee) {
  const normalized = normalizeEmployee(employee || {});
  return {
    employeeId: normalized.employeeId || "",
    employeeCode: normalized.employeeCode || "",
    firstName: normalized.firstName || "",
    middleName: normalized.middleName || "",
    lastName: normalized.lastName || "",
    gender: normalized.gender || "",
    dateOfBirth: normalized.dateOfBirth || "",
    bloodGroup: normalized.bloodGroup || "",
    maritalStatus: normalized.maritalStatus || "",
    employmentType: normalized.employmentType || "",
    employmentStatus: normalized.employmentStatus || "Active",
    joiningDate: normalized.joiningDate || "",
    probationEndDate: normalized.probationEndDate || "",
    exitDate: normalized.exitDate || "",
    exitReason: normalized.exitReason || "",
    exitRemarks: normalized.exitRemarks || "",
    department: normalized.department || "",
    designation: normalized.designation || "",
    role: normalized.role || "",
    managerId: normalized.managerId || "",
    leadEmail: normalized.leadEmail || "",
    reportingHeadEmail: normalized.reportingHeadEmail || "",
    hrEmails: normalized.hrEmails || [],
    leadEmployeeId: normalized.leadEmployeeId || "",
    reportingHeadEmployeeId: normalized.reportingHeadEmployeeId || "",
    hrEmployeeIds: normalized.hrEmployeeIds || [],
    statusReason: normalized.statusReason || "",
    phoneNumber: normalized.phoneNumber || "",
    email: normalized.email || "",
    officeEmail: normalized.officeEmail || "",
    alternatePhone: normalized.alternatePhone || "",
    officePhone: normalized.officePhone || "",
    emergencyContactName: normalized.emergencyContactName || "",
    emergencyContactNumber: normalized.emergencyContactNumber || "",
    addressLine1: normalized.addressLine1 || "",
    addressLine2: normalized.addressLine2 || "",
    city: normalized.city || "",
    state: normalized.state || "",
    country: normalized.country || "India",
    postalCode: normalized.postalCode || "",
    domainUsername: normalized.domainUsername || "",
    status: normalized.status || "Active",
    notes: normalized.notes || "",
    assignedAssets: normalized.assignedAssets || "",
    workLocation: normalized.workLocation || "",
    workMode: normalized.workMode || "",
    shiftTiming: normalized.shiftTiming || "",
    timeZone: normalized.timeZone || "Asia/Kolkata",
    businessUnit: normalized.businessUnit || "",
    team: normalized.team || "",
    region: normalized.region || "",
    client: normalized.client || "",
    panNumber: normalized.panNumber || "",
    aadhaarNumber: normalized.aadhaarNumber || "",
    esiNumber: normalized.esiNumber || "",
    uanNumber: normalized.uanNumber || "",
    bankName: normalized.bankName || "",
    ifscCode: normalized.ifscCode || "",
    accountNumber: normalized.accountNumber || "",
    branch: normalized.branch || "",
  };
}

function EmployeeForm({ employee, mode, onBack, onSave, token, dropdowns }) {
  const [activeTab, setActiveTab] = useState("basic");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [educationList, setEducationList] = useState(
    () => (employee?.educationHistory && employee.educationHistory.length > 0)
      ? employee.educationHistory
      : [{ qualification: "", courseName: "", institute: "", percentage: "" }]
  );
  const [workHistory, setWorkHistory] = useState(() => (employee?.workHistory || []).map(wh => ({ ...wh, isExpanded: false })));
  const [documents, setDocuments] = useState(employee?.documents || []);
  const [form, setForm] = useState(() => buildFormState(employee));
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [profilePic, setProfilePic] = useState({ file: null, previewUrl: employee?.profilePicUrl || "" });
  const [leadDisplay, setLeadDisplay] = useState("");
  const [reportingHeadDisplay, setReportingHeadDisplay] = useState("");
  const [hrDisplays, setHrDisplays] = useState([]);
  const shiftOptions = dropdowns?.SHIFTS || [];
  const [loadingData, setLoadingData] = useState(mode === "edit");

  // Next tab navigation
  const handleNext = useCallback(() => {
    const tabIds = FORM_TABS.map(t => t.id);
    const currentIdx = tabIds.indexOf(activeTab);
    if (currentIdx < tabIds.length - 1) {
      setActiveTab(tabIds[currentIdx + 1]);
    }
  }, [activeTab]);

  // Prev tab navigation
  const handlePrev = useCallback(() => {
    const tabIds = FORM_TABS.map(t => t.id);
    const currentIdx = tabIds.indexOf(activeTab);
    if (currentIdx > 0) {
      setActiveTab(tabIds[currentIdx - 1]);
    }
  }, [activeTab]);

  const isLastTab = FORM_TABS[FORM_TABS.length - 1].id === activeTab;
  const isFirstTab = FORM_TABS[0].id === activeTab;

  const handleProbationAutoSet = useCallback((probDate) => {
    setForm(prev => ({ ...prev, probationEndDate: probDate }));
    toast(`Probation end date auto-set to ${fmtDateFull(probDate)}`);
  }, [toast]);

  // Fetch full details if in edit mode
  useEffect(() => {
    let active = true;
    if (mode === "edit" && employee) {
      const empId = employee.employeeId || employee.employee_id || employee.id || employee.code;
      if (!empId) {
        setLoadingData(false);
        return;
      }
      Promise.all([
        api.getEmployee(empId, token).catch(() => null),
        api.getDocuments(empId, token).catch(() => []),
        api.getWorkHistory(empId, token).catch(() => [])
      ]).then(([empData, docsData, whData]) => {
        if (!active) return;
        if (empData) {
          const normalized = normalizeEmployee(empData);
          setForm(buildFormState(normalized));
          setEducationList(normalized.educationHistory?.length > 0 ? normalized.educationHistory : [{ qualification: "", courseName: "", institute: "", percentage: "" }]);
          setProfilePic(prev => ({ ...prev, previewUrl: normalized.profilePicUrl || "" }));
        }
        if (docsData) setDocuments(docsData);
        if (whData) setWorkHistory(Array.isArray(whData) ? whData.map(wh => ({ ...wh, isExpanded: false })) : []);
        setLoadingData(false);
      });
    } else {
      setLoadingData(false);
    }
    return () => { active = false; };
  }, [mode, employee?.employeeId, employee?.employee_id, employee?.id, employee?.code, token]);

  useEffect(() => {
    let active = true;
    if (!token) return;

    api.getEmployees({ page: 1, limit: 9999 }, token)
      .then((result) => {
        if (!active) return;
        const employees = Array.isArray(result)
          ? result
          : (result?.items || result?.data || result?.employees || []);

        if (mode === "create") {
          const prefix = "SFW-IN-";
          let maxNum = 0;
          employees.forEach(e => {
            const id = e.employeeId || e.employee_id || e.id || e.code;
            if (id && typeof id === "string" && id.startsWith(prefix)) {
              const numStr = id.slice(prefix.length);
              const num = parseInt(numStr, 10);
              if (!isNaN(num) && num > maxNum) {
                maxNum = num;
              }
            }
          });
          const nextId = `${prefix}${String(maxNum + 1).padStart(4, "0")}`;
          setForm(prev => prev.employeeId ? prev : { ...prev, employeeId: nextId });
        }

        setEmployeeOptions(
          employees
            .map((emp) => normalizeEmployee(emp))
            .map((emp) => ({
              value: emp.employeeId || "",
              label: `${fullName(emp)}${emp.employeeId ? ` (${emp.employeeId})` : ""}`,
              email: emp.email || "",
              officeEmail: emp.officeEmail || "",
              department: emp.department || "",
            }))
            .filter((item) => item.value)
        );
      })
      .catch(() => {
        setEmployeeOptions([]);
      });

    return () => { active = false; };
  }, [token]);

  useEffect(() => {
    if (!employee || employeeOptions.length === 0) return;
    const leadEmail = employee.leadEmail || employee.lead_email || "";
    const reportingHeadEmail = employee.reportingHeadEmail || employee.reporting_head_email || "";
    const hrEmails = employee.hrEmployeeIds?.length ? [] : (employee.hrEmails || employee.hr_emails || []);

    setForm(prev => {
      const updates = {};
      if (!prev.leadEmployeeId && leadEmail) {
        const match = employeeOptions.find(opt => opt.officeEmail?.toLowerCase() === leadEmail?.toLowerCase() || opt.email?.toLowerCase() === leadEmail?.toLowerCase());
        if (match) updates.leadEmployeeId = match.value;
      }
      if (!prev.reportingHeadEmployeeId && reportingHeadEmail) {
        const match = employeeOptions.find(opt => opt.officeEmail?.toLowerCase() === reportingHeadEmail?.toLowerCase() || opt.email?.toLowerCase() === reportingHeadEmail?.toLowerCase());
        if (match) updates.reportingHeadEmployeeId = match.value;
      }
      if ((!prev.hrEmployeeIds || prev.hrEmployeeIds.length === 0) && hrEmails.length) {
        const hrIds = hrEmails
          .map(email => employeeOptions.find(opt => opt.officeEmail?.toLowerCase() === email?.toLowerCase() || opt.email?.toLowerCase() === email?.toLowerCase())?.value)
          .filter(Boolean);
        if (hrIds.length) updates.hrEmployeeIds = hrIds;
      }
      return Object.keys(updates).length ? { ...prev, ...updates } : prev;
    });
  }, [employee, employeeOptions]);

  // Update displays when selections change
  useEffect(() => {
    if (form.leadEmployeeId && employeeOptions.length) {
      const opt = employeeOptions.find(o => o.value === form.leadEmployeeId);
      setLeadDisplay(opt ? opt.label : "");
    } else {
      setLeadDisplay("");
    }
  }, [form.leadEmployeeId, employeeOptions]);

  useEffect(() => {
    if (form.reportingHeadEmployeeId && employeeOptions.length) {
      const opt = employeeOptions.find(o => o.value === form.reportingHeadEmployeeId);
      setReportingHeadDisplay(opt ? opt.label : "");
    } else {
      setReportingHeadDisplay("");
    }
  }, [form.reportingHeadEmployeeId, employeeOptions]);

  useEffect(() => {
    if (form.hrEmployeeIds.length && employeeOptions.length) {
      const displays = form.hrEmployeeIds.map(id => {
        const opt = employeeOptions.find(o => o.value === id);
        return opt ? opt.label : id;
      });
      setHrDisplays(displays);
    } else {
      setHrDisplays([]);
    }
  }, [form.hrEmployeeIds, employeeOptions]);

  const handleMultiSelectChange = useCallback((e) => {
    const values = Array.from(e.target.selectedOptions).map((option) => option.value);
    setForm((prev) => ({ ...prev, [e.target.name]: values }));
  }, []);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleTabSwitch = useCallback((targetTab) => {
    setActiveTab(targetTab);
  }, []);

  const validate = () => {
    const e = {};
    if (!form.firstName?.trim()) e.firstName = "First name required";
    if (!form.lastName?.trim()) e.lastName = "Last name required";
    if (!form.employeeId?.trim()) e.employeeId = "Employee ID required";
    if (!form.department?.trim()) e.department = "Department required";
    if (!form.designation?.trim()) e.designation = "Designation required";
    if (!form.joiningDate) e.joiningDate = "Joining date required";
    if (!form.phoneNumber?.trim()) e.phoneNumber = "Phone required";
    if (!form.email?.trim()) e.email = "Email required";
    if (!form.addressLine1?.trim()) e.addressLine1 = "Address required";
    if (!form.city?.trim()) e.city = "City required";
    if (!form.state?.trim()) e.state = "State required";
    if (!form.emergencyContactName?.trim()) e.emergencyContactName = "Emergency contact name required";
    if (!form.emergencyContactNumber?.trim()) e.emergencyContactNumber = "Emergency phone required";
    if (!form.workLocation?.trim()) e.workLocation = "Work location required";
    if (!form.workMode) e.workMode = "Work mode required";
    if (String(form.employmentStatus).toLowerCase() === "inactive" && !form.statusReason?.trim())
      e.statusReason = "Status reason required";
    setErrors(e);
    return e;
  };

  const handleSubmit = async () => {
    const currentErrors = validate();
    if (Object.keys(currentErrors).length > 0) {
      const errorMsg = Object.values(currentErrors).join(", ");
      toast.error(`Please fix: ${errorMsg}`);
      return;
    }
    setIsSubmitting(true);
    try {
      const selectedLead = employeeOptions.find(opt => opt.value === form.leadEmployeeId);
      const selectedReportingHead = employeeOptions.find(opt => opt.value === form.reportingHeadEmployeeId);
      const selectedHRs = employeeOptions.filter(opt => form.hrEmployeeIds.includes(opt.value));
      const payload = {
        employee_id: form.employeeId,
        employee_code: form.employeeCode || null,
        first_name: form.firstName,
        last_name: form.lastName,
        Middle_Name: form.middleName || null,
        gender: form.gender || null,
        marital_status: form.maritalStatus || null,
        date_of_birth: form.dateOfBirth || null,
        blood_group: form.bloodGroup || null,
        department: form.department,
        designation: form.designation,
        role: form.role || null,
        employment_type: form.employmentType || null,
        employment_status: form.employmentStatus || "Active",
        joining_date: form.joiningDate,
        probation_end_date: form.probationEndDate || null,
        exit_date: form.exitDate || null,
        exit_reason: form.exitReason || null,
        exit_remarks: form.exitRemarks || null,
        work_mode: form.workMode,
        shift_timing: form.shiftTiming || null,
        work_location: form.workLocation || null,
        region: form.region || null,
        client: form.client || null,
        email: form.email,
        office_email: form.officeEmail || null,
        phone_number: form.phoneNumber,
        alternate_phone: form.alternatePhone || null,
        office_phone: form.officePhone || null,
        emergency_contact_name: form.emergencyContactName,
        emergency_contact_number: form.emergencyContactNumber,
        manager_id: form.managerId || null,
        lead_id: form.leadEmployeeId || null,
        reporting_head_id: form.reportingHeadEmployeeId || null,
        hr_ids: form.hrEmployeeIds || [],
        lead_email: selectedLead?.officeEmail || selectedLead?.email || form.leadEmail || null,
        reporting_head_email: selectedReportingHead?.officeEmail || selectedReportingHead?.email || form.reportingHeadEmail || null,
        hr_emails: selectedHRs.length > 0 ? selectedHRs.map(opt => opt.officeEmail || opt.email).filter(Boolean) : (form.hrEmails || []),
        address_line1: form.addressLine1 || null,
        address_line2: form.addressLine2 || null,
        city: form.city || null,
        state: form.state || null,
        country: form.country || "India",
        postal_code: form.postalCode || null,
        domain_username: form.domainUsername || null,
        status: form.status || "Active",
        status_reason: form.statusReason || null,
        notes: form.notes || null,
        assigned_assets: form.assignedAssets || null,
        pan_number: form.panNumber || null,
        aadhaar_number: form.aadhaarNumber || null,
        esi_number: form.esiNumber || null,
        uan_number: form.uanNumber || null,
        bank_name: form.bankName || null,
        ifsc_code: form.ifscCode || null,
        account_number: form.accountNumber || null,
        bank_branch: form.branch || null,
        highest_qualification: educationList[0]?.qualification || null,
        institute_name: educationList[0]?.institute || null,
        percentage: educationList[0]?.percentage || null,
        education_history: educationList.filter(e => e.qualification || e.institute),
      };

      let savedEmployee;
      if (mode === "edit") {
        // Use the original ID for the URL but the form payload can have the same or updated values
        const targetId = employee?.employeeId || form.employeeId;
        savedEmployee = await api.updateEmployee(targetId, payload, token);
      } else {
        savedEmployee = await api.createEmployee(payload, token);
      }

      const empId = savedEmployee?.employee_id || form.employeeId;


      if (profilePic.file && empId) {
        try { await api.uploadProfilePic(empId, profilePic.file, token); }
        catch { toast("Employee saved but profile picture upload failed"); }
      }

      for (const wh of workHistory) {
        if (String(wh.id).startsWith("wh_")) {
          const whPayload = {
            companyType: wh.companyType,
            companyName: wh.companyName || null,
            companyAddress: wh.companyAddress || null,
            referenceEmail: wh.referenceEmail || null,
            role: wh.role,
            designation: wh.designation || null,
            department: wh.department || null,
            employmentType: wh.employmentType || null,
            joiningDate: wh.joiningDate,
            // FIX 2: send null when currentlyWorking is true
            relievingDate: wh.currentlyWorking ? null : (wh.relievingDate || null),
            currentlyWorking: wh.currentlyWorking || false,
            responsibilities: wh.responsibilities || null,
          };
          const savedWh = await api.createWorkHistory(empId, whPayload, token);
          for (const doc of (wh.documents || [])) {
            if (doc.file) {
              await api.uploadWorkHistoryDoc(empId, savedWh.id, doc.file, doc.docType, doc.docLabel, token);
            }
          }
        }
      }

      for (const doc of documents) {
        if (doc.file) {
          await api.uploadDocument(empId, doc.file, doc.docType, doc.docLabel, token);
        }
      }

      toast.success(
        mode === "edit"
          ? `${form.firstName} ${form.lastName} updated successfully`
          : `${form.firstName} ${form.lastName} created successfully`);
      onSave(normalizeEmployee({ ...savedEmployee, employee_id: empId }));
    } catch (e) {
      toast.error(e.message || "Save failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  const tabHasError = (tabId) => {
    const tabFields = {
      basic: ["firstName", "lastName", "gender", "dateOfBirth"],
      employment: ["employeeId", "employmentType", "joiningDate", "department", "designation", "statusReason"],
      contact: ["phoneNumber", "email", "addressLine1", "city", "state", "emergencyContactName", "emergencyContactNumber"],
      bank: ["panNumber", "aadhaarNumber", "bankName", "ifscCode", "accountNumber"],
      work: ["workLocation", "workMode"],
    };
    return (tabFields[tabId] || []).some(f => errors[f]);
  };

  const genderOptions = dropdowns?.GENDERS || [];
  const employmentStatusOpts = dropdowns?.EMPLOYMENT_STATUS || [];
  const roleOptions = dropdowns?.ROLE || [];
  const bloodGroupOptions = dropdowns?.BLOOD_GROUP || [];
  const maritalStatusOptions = dropdowns?.MARITAL_STATUS || [];
  const workModeOptions = dropdowns?.WORK_MODE || [];
  const empTypeOptions = dropdowns?.EMPLOYEE_TYPE || [];
  const designationOptions = dropdowns?.DESIGNATION || [];
  const departmentOptions = dropdowns?.DEPARTMENT || [];

  return (
    <>
      <div className="em-page-header">
        <div className="em-page-header__title">
          <h2>{mode === "create" ? "Add New Employee" : "Edit Employee"}</h2>
          <p>{mode === "create" ? "Fill in the details to create a new record" : `Editing ${fullName(employee || {})}`}</p>
        </div>
        <button className="em-btn em-btn--outline em-btn--sm" onClick={onBack}><ArrowLeft size={14} /> Back</button>
      </div>

      {loadingData ? (
        <div className="em-empty" style={{ padding: "48px 0" }}>
          <RefreshCw size={28} style={{ opacity: 0.4, animation: "spin 1s linear infinite" }} />
          <p>Loading full employee data...</p>
        </div>
      ) : (
        <>
          <div className="em-tabs">
            {FORM_TABS.map(({ id, label, Icon }) => (
              <button key={id} className={`em-tab-btn${activeTab === id ? " active" : ""}${tabHasError(id) ? " em-tab-btn--error" : ""}`} onClick={() => handleTabSwitch(id)} type="button">
                <Icon size={15} />{label}
                {tabHasError(id) && <span className="em-tab-error-dot" />}
              </button>
            ))}
          </div>

          {/* ── Basic Tab ── */}
          {activeTab === "basic" && (
            <div className="em-form-card">
              <div className="em-form-card__title"><User size={15} /> Basic Information</div>
              <div className="em-form-section-label" style={{ marginBottom: 12 }}>Profile Picture</div>
              <ProfilePicUpload
                empId={mode === "edit" ? (employee?.employeeId || null) : null}
                currentUrl={profilePic.previewUrl}
                token={token}
                onUpdate={(data) => setProfilePic(prev => ({ ...prev, ...data }))}
              />
              <div className="em-form-divider" />
              <div className="em-stack">
                <div className="em-grid-3">
                  <Field label="First Name" error={errors.firstName} required>
                    <input className={`em-input${errors.firstName ? " is-error" : ""}`} name="firstName" value={form.firstName} onChange={handleChange} placeholder="Enter first name" autoComplete="off" />
                  </Field>
                  <Field label="Middle Name">
                    <input className="em-input" name="middleName" value={form.middleName} onChange={handleChange} placeholder="Enter middle name" autoComplete="off" />
                  </Field>
                  <Field label="Last Name" error={errors.lastName} required>
                    <input className={`em-input${errors.lastName ? " is-error" : ""}`} name="lastName" value={form.lastName} onChange={handleChange} placeholder="Enter last name" autoComplete="off" />
                  </Field>
                </div>
                <div className="em-grid-3">
                  <Field label="Gender" error={errors.gender}>
                    <select className={`em-select-field${errors.gender ? " is-error" : ""}`} name="gender" value={form.gender} onChange={handleChange}>
                      <option value="">Select...</option>
                      {genderOptions.length > 0
                        ? genderOptions.map(g => <option key={g.value} value={g.value}>{g.label}</option>)
                        : <><option value="male">Male</option><option value="female">Female</option><option value="other">Other</option></>
                      }
                    </select>
                  </Field>
                  <Field label="Date of Birth" error={errors.dateOfBirth}>
                    <input className={`em-input${errors.dateOfBirth ? " is-error" : ""}`} type="date" name="dateOfBirth" value={form.dateOfBirth} onChange={handleChange} />
                  </Field>
                  <Field label="Blood Group">
                    <select className="em-select-field" name="bloodGroup" value={form.bloodGroup} onChange={handleChange}>
                      <option value="">Select...</option>
                      {bloodGroupOptions.length > 0
                        ? bloodGroupOptions.map(b => <option key={b.value} value={b.value}>{b.label}</option>)
                        : <><option value="A+">A+</option><option value="A-">A-</option><option value="B+">B+</option><option value="B-">B-</option><option value="AB+">AB+</option><option value="AB-">AB-</option><option value="O+">O+</option><option value="O-">O-</option></>
                      }
                    </select>
                  </Field>
                  <Field label="Marital Status">
                    <select className="em-select-field" name="maritalStatus" value={form.maritalStatus} onChange={handleChange}>
                      <option value="">Select...</option>
                      {maritalStatusOptions.length > 0
                        ? maritalStatusOptions.map(m => <option key={m.value} value={m.value}>{m.label}</option>)
                        : <><option value="single">Single</option><option value="married">Married</option><option value="divorced">Divorced</option></>
                      }
                    </select>
                  </Field>
                </div>
              </div>
            </div>
          )}

          {/* ── Employment Tab ── */}
          {activeTab === "employment" && (
            <div className="em-form-card">
              <div className="em-form-card__title"><Briefcase size={15} /> Employment Details</div>
              <div className="em-stack">
                <div className="em-grid-3">
                  <Field label="Employee ID" error={errors.employeeId} required hint="Auto-generated">
                    <input
                      className={`em-input${errors.employeeId ? " is-error" : ""}`}
                      name="employeeId"
                      value={form.employeeId}
                      readOnly
                      placeholder="Auto-generated..."
                      autoComplete="off"
                      style={{ background: "var(--em-bg-subtle, #f8f9fa)", cursor: "not-allowed", color: "var(--em-text-2, #64748b)", fontWeight: 600 }}
                    />
                  </Field>
                  <Field label="Employee Code">
                    <input className="em-input" name="employeeCode" value={form.employeeCode} onChange={handleChange} placeholder="e.g., EC001" autoComplete="off" />
                  </Field>
                  <Field label="Employment Type" error={errors.employmentType}>
                    <select className={`em-select-field${errors.employmentType ? " is-error" : ""}`} name="employmentType" value={form.employmentType} onChange={handleChange}>
                      <option value="">Select...</option>
                      {empTypeOptions.length > 0
                        ? empTypeOptions.map(t => <option key={t.value} value={t.value}>{t.label}</option>)
                        : <><option value="full-time">Full Time</option><option value="part-time">Part Time</option><option value="contract">Contract</option><option value="intern">Intern</option></>
                      }
                    </select>
                  </Field>
                </div>
                <div className="em-grid-3">
                  <Field label="Employment Status">
                    <select className="em-select-field" name="employmentStatus" value={form.employmentStatus} onChange={handleChange}>
                      <option value="">Select...</option>
                      {employmentStatusOpts.length > 0
                        ? employmentStatusOpts.map(s => <option key={s.value} value={s.value}>{s.label}</option>)
                        : <><option value="Active">Active</option><option value="Inactive">Inactive</option></>
                      }
                    </select>
                  </Field>
                  <Field label="Joining Date" error={errors.joiningDate} required>
                    <input className={`em-input${errors.joiningDate ? " is-error" : ""}`} type="date" name="joiningDate" value={form.joiningDate} onChange={handleChange} />
                  </Field>
                  <Field label="Probation End Date">
                    <input className="em-input" type="date" name="probationEndDate" value={form.probationEndDate} onChange={handleChange} />
                  </Field>
                </div>
                {String(form.employmentStatus).toLowerCase() === "inactive" && (
                  <Field label="Reason for Inactive Status" error={errors.statusReason} required>
                    <textarea className={`em-textarea${errors.statusReason ? " is-error" : ""}`} name="statusReason" value={form.statusReason} onChange={handleChange} placeholder="e.g., Resigned, Contract ended..." rows={2} />
                  </Field>
                )}
                <div className="em-grid-3">
                  <Field label="Exit Date">
                    <input className="em-input" type="date" name="exitDate" value={form.exitDate} onChange={handleChange} />
                  </Field>
                  <Field label="Exit Reason">
                    <select className="em-select-field" name="exitReason" value={form.exitReason} onChange={handleChange}>
                      <option value="">Select reason...</option>
                      <option value="Short Notice">Short Notice</option>
                      <option value="Terminated">Terminated</option>
                      <option value="Left Service">Left Service</option>
                      <option value="Others">Others</option>
                    </select>
                  </Field>
                  <Field label="Department" error={errors.department} required>
                    <SearchableSelect
                      name="department"
                      value={form.department}
                      items={departmentOptions.length > 0 ? departmentOptions : DEPARTMENTS.map(d => ({ value: d, label: d }))}
                      onChange={handleChange}
                      placeholder="Select..."
                    />
                  </Field>
                </div>
                {form.exitReason === "Others" && (
                  <Field label="Exit Remarks">
                    <textarea
                      className="em-textarea"
                      name="exitRemarks"
                      value={form.exitRemarks}
                      onChange={handleChange}
                      placeholder="Enter specific reason for exit..."
                      rows={2}
                    />
                  </Field>
                )}
                <div className="em-grid-3">
                  <Field label="Designation" error={errors.designation} required>
                    <SearchableSelect
                      name="designation"
                      value={form.designation}
                      items={designationOptions.length > 0 ? designationOptions : DESIGNATIONS.map(d => ({ value: d, label: d }))}
                      onChange={handleChange}
                      placeholder="Select..."
                    />
                  </Field>
                  <Field label="Role">
                    <SearchableSelect
                      name="role"
                      value={form.role}
                      items={roleOptions.length > 0 ? roleOptions : DESIGNATIONS.map(d => ({ value: d, label: d }))}
                      onChange={handleChange}
                      placeholder="Select..."
                    />
                  </Field>
                  <Field label="Lead">
                    <SearchableSelect
                      name="leadEmployeeId"
                      value={form.leadEmployeeId}
                      items={employeeOptions}
                      onChange={handleChange}
                      placeholder="Select..."
                    />
                  </Field>
                </div>
                <div className="em-grid-3">
                  <Field label="Reporting Head">
                    <SearchableSelect
                      name="reportingHeadEmployeeId"
                      value={form.reportingHeadEmployeeId}
                      items={employeeOptions}
                      onChange={handleChange}
                      placeholder="Select..."
                    />
                  </Field>
                  <Field label="HR" hint="Multiple HR contacts">
                    <MultiSelectDropdown
                      items={employeeOptions.filter(opt => {
                        const deptLabel = resolveLabel(opt.department, dropdowns?.DEPARTMENT) || "";
                        const lowerDept = String(deptLabel).toLowerCase();
                        return lowerDept === 'hr' || lowerDept.includes('human resource');
                      })}
                      selectedValues={form.hrEmployeeIds}
                      onChange={(values) => setForm((prev) => ({ ...prev, hrEmployeeIds: values }))}
                      placeholder="Select HR contacts..."
                    />
                  </Field>
                </div>
              </div>
            </div>
          )}
          {/* ── Education Tab ── */}
          {activeTab === "education" && (
            <div className="em-form-card">
              <div className="em-form-card__title" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}><GraduationCap size={15} /> Educational Details</span>
                <button
                  type="button"
                  className="em-btn em-btn--primary em-btn--sm"
                  onClick={() => setEducationList(prev => [...prev, { qualification: "", courseName: "", institute: "", percentage: "" }])}
                >
                  <Plus size={14} /> Add
                </button>
              </div>
              <div className="em-stack">
                {educationList.map((edu, idx) => (
                  <div key={idx} style={{ border: "1px solid var(--em-border)", borderRadius: 10, padding: "14px 16px", position: "relative", marginBottom: 8, background: "var(--em-bg-card)" }}>
                    {educationList.length > 1 && (
                      <button
                        type="button"
                        style={{ position: "absolute", top: 10, right: 10, background: "none", border: "none", cursor: "pointer", color: "#ef4444" }}
                        onClick={() => setEducationList(prev => prev.filter((_, i) => i !== idx))}
                        title="Remove"
                      >
                        <Trash2 size={15} />
                      </button>
                    )}
                    <div className="em-grid-2" style={{ marginBottom: 10 }}>
                      <Field label="Qualification">
                        <SearchableSelect
                          name={`edu_qualification_${idx}`}
                          value={edu.qualification}
                          items={QUALIFICATIONS}
                          onChange={e => setEducationList(prev => prev.map((r, i) => i === idx ? { ...r, qualification: e.target.value } : r))}
                          placeholder="Select qualification..."
                        />
                      </Field>
                      <Field label="Percentage / CGPA">
                        <input
                          className="em-input"
                          value={edu.percentage}
                          onChange={e => setEducationList(prev => prev.map((r, i) => i === idx ? { ...r, percentage: e.target.value } : r))}
                          placeholder="e.g. 85%"
                        />
                      </Field>
                    </div>
                    <div className="em-grid-2" style={{ marginBottom: 10 }}>
                      <Field label={<span style={{ display: "flex", alignItems: "center", gap: 5 }}><BookOpen size={13} /> Course / Specialization</span>}>
                        <input
                          className="em-input"
                          value={edu.courseName || ""}
                          onChange={e => setEducationList(prev => prev.map((r, i) => i === idx ? { ...r, courseName: e.target.value } : r))}
                          placeholder="e.g. B.E Computer Science"
                        />
                      </Field>
                      <Field label="Name of Institute">
                        <input
                          className="em-input"
                          value={edu.institute}
                          onChange={e => setEducationList(prev => prev.map((r, i) => i === idx ? { ...r, institute: e.target.value } : r))}
                          placeholder="Enter college/school name"
                        />
                      </Field>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Bank & Statutory Tab ── */}
          {activeTab === "bank" && (
            <div className="em-form-card">
              <div className="em-form-card__title"><CreditCard size={15} /> Statutory & Bank Details</div>
              <div className="em-stack">
                <div className="em-form-section-label"><FileText size={14} /> Statutory Details</div>
                <div className="em-grid-3">
                  <Field label="PAN Number">
                    <input className="em-input" name="panNumber" value={form.panNumber} onChange={handleChange} placeholder="ABCDE1234F" autoComplete="off" />
                  </Field>
                  <Field label="Aadhaar Number">
                    <input className="em-input" name="aadhaarNumber" value={form.aadhaarNumber} onChange={handleChange} placeholder="1234 5678 9012" autoComplete="off" />
                  </Field>
                  <Field label="ESI Number">
                    <input className="em-input" name="esiNumber" value={form.esiNumber} onChange={handleChange} placeholder="ESI Number" autoComplete="off" />
                  </Field>
                  <Field label="UAN Number">
                    <input className="em-input" name="uanNumber" value={form.uanNumber} onChange={handleChange} placeholder="UAN Number" autoComplete="off" />
                  </Field>
                </div>

                <div className="em-form-divider" />
                <div className="em-form-section-label"><CreditCard size={14} /> Bank Details</div>
                <div className="em-grid-3">
                  <Field label="Bank Name">
                    <input className="em-input" name="bankName" value={form.bankName} onChange={handleChange} placeholder="e.g., HDFC Bank" autoComplete="off" />
                  </Field>
                  <Field label="IFSC Code">
                    <input className="em-input" name="ifscCode" value={form.ifscCode} onChange={handleChange} placeholder="HDFC0001234" autoComplete="off" />
                  </Field>
                  <Field label="Account Number">
                    <input className="em-input" name="accountNumber" value={form.accountNumber} onChange={handleChange} placeholder="Account Number" autoComplete="off" />
                  </Field>
                  <Field label="Branch">
                    <input className="em-input" name="branch" value={form.branch} onChange={handleChange} placeholder="Branch Name" autoComplete="off" />
                  </Field>
                </div>
              </div>
            </div>
          )}

          {/* ── Contact Tab ── */}
          {activeTab === "contact" && (
            <div className="em-stack">
              <div className="em-form-card">
                <div className="em-form-card__title"><Phone size={15} /> Contact Details</div>
                <div className="em-stack">
                  <div className="em-grid-3">
                    <Field label="Phone Number" error={errors.phoneNumber} required>
                      <input className={`em-input${errors.phoneNumber ? " is-error" : ""}`} name="phoneNumber" value={form.phoneNumber} onChange={handleChange} placeholder="+91 9876543210" autoComplete="off" />
                    </Field>
                    <Field label="Alternate Phone">
                      <input className="em-input" name="alternatePhone" value={form.alternatePhone} onChange={handleChange} placeholder="+91 9876543211" autoComplete="off" />
                    </Field>
                    <Field label="Personal Email" error={errors.email} required>
                      <input className={`em-input${errors.email ? " is-error" : ""}`} type="email" name="email" value={form.email} onChange={handleChange} placeholder="personal@email.com" autoComplete="off" />
                    </Field>
                    <Field label="Office Email">
                      <input className="em-input" type="email" name="officeEmail" value={form.officeEmail} onChange={handleChange} placeholder="name@company.com" autoComplete="off" />
                    </Field>
                  </div>
                </div>
              </div>
              <div className="em-form-card">
                <div className="em-form-card__title"><Building2 size={15} /> Address</div>
                <div className="em-stack">
                  <Field label="Address Line 1" error={errors.addressLine1} required>
                    <input className={`em-input${errors.addressLine1 ? " is-error" : ""}`} name="addressLine1" value={form.addressLine1} onChange={handleChange} placeholder="Street address" autoComplete="off" />
                  </Field>
                  <Field label="Address Line 2">
                    <input className="em-input" name="addressLine2" value={form.addressLine2} onChange={handleChange} placeholder="Apartment, suite, etc." autoComplete="off" />
                  </Field>
                  <div className="em-grid-3">
                    <Field label="City" error={errors.city} required>
                      <input className={`em-input${errors.city ? " is-error" : ""}`} name="city" value={form.city} onChange={handleChange} placeholder="City" autoComplete="off" />
                    </Field>
                    <Field label="State" error={errors.state} required>
                      <input className={`em-input${errors.state ? " is-error" : ""}`} name="state" value={form.state} onChange={handleChange} placeholder="State" autoComplete="off" />
                    </Field>
                    <Field label="Country">
                      <input className="em-input" name="country" value={form.country} onChange={handleChange} placeholder="Country" autoComplete="off" />
                    </Field>
                    <Field label="Postal Code">
                      <input className="em-input" name="postalCode" value={form.postalCode} onChange={handleChange} placeholder="123456" autoComplete="off" />
                    </Field>
                  </div>
                </div>
              </div>
              <div className="em-form-card">
                <div className="em-form-card__title"><AlertTriangle size={15} /> Emergency Contact</div>
                <div className="em-grid-2">
                  <Field label="Contact Name" error={errors.emergencyContactName} required>
                    <input name="emergencyContactName" value={form.emergencyContactName} onChange={handleChange} className={`em-input${errors.emergencyContactName ? " is-error" : ""}`} placeholder="Emergency contact name" autoComplete="off" />
                  </Field>
                  <Field label="Contact Phone" error={errors.emergencyContactNumber} required>
                    <input name="emergencyContactNumber" value={form.emergencyContactNumber} onChange={handleChange} className={`em-input${errors.emergencyContactNumber ? " is-error" : ""}`} placeholder="+91 9876543210" autoComplete="off" />
                  </Field>
                </div>
              </div>
            </div>
          )}

          {/* ── Documents Tab ── */}
          {activeTab === "documents" && (
            <DocumentsSection documents={documents} onChange={setDocuments} />
          )}

          {/* ── Work Details Tab ── */}
          {activeTab === "work" && (
            <div className="em-stack">
              <div className="em-form-card">
                <div className="em-form-card__title"><Settings size={15} /> Work Details</div>
                <div className="em-stack">
                  <div className="em-grid-2">
                    <Field label="Work Location" error={errors.workLocation} required>
                      <input className={`em-input${errors.workLocation ? " is-error" : ""}`} name="workLocation" value={form.workLocation} onChange={handleChange} placeholder="e.g., Chennai Office" autoComplete="off" />
                    </Field>
                    <Field label="Work Mode" error={errors.workMode} required>
                      <select className={`em-select-field${errors.workMode ? " is-error" : ""}`} name="workMode" value={form.workMode} onChange={handleChange}>
                        <option value="">Select...</option>
                        {workModeOptions.length > 0
                          ? workModeOptions.map(w => <option key={w.value} value={w.value}>{w.label}</option>)
                          : <><option value="office">Office</option><option value="wfh">Work from Home</option><option value="hybrid">Hybrid</option></>
                        }
                      </select>
                    </Field>
                  </div>
                  <div className="em-grid-2">
                    <Field label="Shift Timing" hint={shiftOptions.length === 0 ? "No shifts found in master" : ""}>
                      <select
                        className="em-select-field"
                        name="shiftTiming"
                        value={form.shiftTiming || ""}
                        onChange={handleChange}
                      >
                        <option value="">Select Timing...</option>
                        {shiftOptions.map(s => {
                          const formatTime = (timeStr) => {
                            if (!timeStr) return "";
                            const [h, m] = timeStr.split(':');
                            const hours = parseInt(h, 10);
                            const ampm = hours >= 12 ? 'PM' : 'AM';
                            const displayHours = hours % 12 || 12;
                            return `${displayHours}:${m} ${ampm}`;
                          };
                          const formattedStart = formatTime(s.startTime);
                          const formattedEnd = formatTime(s.endTime);

                          return (
                            <option key={s.value} value={s.value}>
                              {s.label}{formattedStart && formattedEnd ? ` (${formattedStart} – ${formattedEnd})` : ""}
                            </option>
                          );
                        })}
                      </select>
                    </Field>
                    <Field label="Time Zone">
                      <select className="em-select-field" name="timeZone" value={form.timeZone} onChange={handleChange}>
                        <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                        <option value="America/New_York">America/New_York (EST)</option>
                        <option value="America/Los_Angeles">America/Los_Angeles (PST)</option>
                        <option value="Europe/London">Europe/London (GMT)</option>
                      </select>
                    </Field>
                  </div>
                  <div className="em-grid-2">
                    <Field label="Business Unit">
                      <input className="em-input" name="businessUnit" value={form.businessUnit} onChange={handleChange} placeholder="e.g., Technology" autoComplete="off" />
                    </Field>
                    <Field label="Team">
                      <input className="em-input" name="team" value={form.team} onChange={handleChange} placeholder="e.g., Backend Team" autoComplete="off" />
                    </Field>
                  </div>
                </div>
              </div>
              <WorkExperienceSection
                experiences={workHistory}
                onChange={setWorkHistory}
                dropdowns={dropdowns}
                onProbationChange={handleProbationAutoSet}
              />
            </div>
          )}

          <div className="em-form-footer" style={{ justifyContent: "flex-end" }}>
            <div style={{ display: "flex", gap: 8 }}>
              {!isFirstTab && (
                <button className="em-btn em-btn--outline" type="button" onClick={handlePrev}>
                  <ChevronLeft size={15} /> Previous
                </button>
              )}
              {!isLastTab && (
                <button className="em-btn em-btn--outline" type="button" onClick={handleNext}>
                  Next <ChevronRight size={15} />
                </button>
              )}
              <button className="em-btn em-btn--primary" type="button" onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? "Saving..." : mode === "create" ? "Save Employee" : "Update Employee"}
              </button>
              <button className="em-btn em-btn--outline" type="button" onClick={onBack}>Cancel</button>
            </div>
          </div>
        </>
      )}
    </>
  );
}

// ── Main Export ───────────────────────────────────────────────────────────────
export default function EmployeeMaster() {
  const [allEmployees, setAllEmployees] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: "", department: "all", designation: "all", status: "all", workMode: "all", role: "all" });
  const [view, setView] = useState("list");
  const [selected, setSelected] = useState(null);
  const [sidebarOpen, setSidebar] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [overallStats, setOverallStats] = useState({ active: 0, inactive: 0 });

  const { user, token, logout } = useAuth();
  const canManage = String(user?.role || "").toUpperCase() === "ADMIN";
  const { dropdowns } = useDropdowns(token);

  const fetchAllEmployees = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getEmployees({ page: 1, limit: 10000 }, token);
      let normalized = (data.employees || (Array.isArray(data) ? data : [])).map(normalizeEmployee);

      const userRole = String(user?.role || "").toUpperCase();
      if (userRole !== "ADMIN") {
        normalized = normalized.filter(emp =>
          (user?.email && emp.email === user?.email) ||
          (user?.empId && (String(emp.employeeId) === String(user?.empId) || String(emp.id) === String(user?.empId))) ||
          (user?.employeeId && (String(emp.employeeId) === String(user?.employeeId) || String(emp.id) === String(user?.employeeId)))
        );
      }

      setAllEmployees(normalized);
    } catch (e) {
      toast.error(e.message || "Failed to load employees");
    } finally {
      setLoading(false);
    }
  }, [token, user]);

  useEffect(() => {
    if (view === "list") {
      fetchAllEmployees();
    }
  }, [view, fetchAllEmployees]);

  useEffect(() => {
    let filtered = allEmployees;

    if (filters.search) {
      const s = filters.search.toLowerCase();
      filtered = filtered.filter(emp =>
        fullName(emp).toLowerCase().includes(s) ||
        (emp.employeeId || "").toLowerCase().includes(s) ||
        (emp.email || "").toLowerCase().includes(s)
      );
    }

    if (filters.department && filters.department !== "all") {
      const deptOpt = dropdowns?.DEPARTMENT?.find(d => String(d.value) === String(filters.department));
      const deptLabel = deptOpt ? deptOpt.label : filters.department;
      filtered = filtered.filter(emp => String(emp.department) === String(filters.department) || emp.department === deptLabel);
    }

    if (filters.designation && filters.designation !== "all") {
      const desigOpt = dropdowns?.DESIGNATION?.find(d => String(d.value) === String(filters.designation));
      const desigLabel = desigOpt ? desigOpt.label : filters.designation;
      filtered = filtered.filter(emp => String(emp.designation) === String(filters.designation) || emp.designation === desigLabel);
    }

    if (filters.status && filters.status !== "all") {
      filtered = filtered.filter(emp => String(emp.employmentStatus).toLowerCase() === String(filters.status).toLowerCase());
    }

    if (filters.workMode && filters.workMode !== "all") {
      filtered = filtered.filter(emp => String(emp.workMode).toLowerCase() === String(filters.workMode).toLowerCase());
    }

    if (filters.role && filters.role !== "all") {
      filtered = filtered.filter(emp => String(emp.role).toLowerCase() === String(filters.role).toLowerCase());
    }

    setOverallStats({
      active: filtered.filter(e => String(e.employmentStatus).toLowerCase() === "active").length,
      inactive: filtered.filter(e => String(e.employmentStatus).toLowerCase() === "inactive").length
    });

    setTotal(filtered.length);
    const startIndex = (page - 1) * rowsPerPage;
    setEmployees(filtered.slice(startIndex, startIndex + rowsPerPage));
  }, [allEmployees, filters, page, rowsPerPage, dropdowns]);

  const handleFilterChange = useCallback((newFilters) => {
    setFilters(newFilters);
    setPage(1);
  }, []);

  const navigate = useCallback((viewName, emp = null) => {
    setSelected(emp);
    setView(viewName);
  }, []);

  const handleSave = useCallback((savedEmp) => {
    const reset = { search: "", department: "all", designation: "all", status: "all", workMode: "all", role: "all" };
    setPage(1);
    setFilters(reset);
    if (view === "edit" && savedEmp) {
      navigate("detail", savedEmp);
    } else {
      navigate("list");
    }
    setTimeout(() => fetchAllEmployees(), 100);
  }, [navigate, fetchAllEmployees, view]);

  const handleConfirmDelete = async () => {
    if (!confirmDelete || !token) return;
    setIsDeleting(true);
    try {
      await api.deleteEmployee(confirmDelete.employeeId, token);
      toast.success(`${fullName(confirmDelete)} deleted successfully`);
      setConfirmDelete(null);
      fetchAllEmployees();
    } catch (e) {
      toast.error(e.message || "Failed to delete employee");
    } finally {
      setIsDeleting(false);
    }
  };

  const topbar = {
    list: { title: "Employee Master", sub: "Manage your organization's employees" },
    detail: { title: "Employee Profile", sub: selected ? fullName(selected) : "" },
    create: { title: "Add New Employee", sub: "Create a new employee record" },
    edit: { title: "Edit Employee", sub: selected ? `Editing ${fullName(selected)}` : "" },
  }[view];

  return (
    <>
      <MainLayout
        sidebarOpen={sidebarOpen}
        toggleSidebar={() => setSidebar(p => !p)}
        title={topbar.title}
        subtitle={topbar.sub}
        showSearch={true}
      >
        <div style={{ padding: '0' }}>
          <main className="em-content" style={{ padding: 0, gap: '20px' }}>
            {view === "list" && (
              <EmployeeTable
                employees={employees}
                dropdowns={dropdowns}
                loading={loading}
                total={total}
                page={page}
                onPageChange={setPage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(val) => { setRowsPerPage(val); setPage(1); }}
                filters={{ ...filters, overallActive: overallStats.active, overallInactive: overallStats.inactive }}
                onFilterChange={handleFilterChange}
                onView={emp => navigate("detail", emp)}
                onEdit={canManage ? emp => navigate("edit", emp) : null}
                onDelete={canManage ? emp => setConfirmDelete(emp) : null}
                onAdd={canManage ? () => navigate("create") : null}
              />
            )}
            {view === "detail" && selected && (
              <EmployeeDetail
                employee={selected}
                onBack={() => navigate("list")}
                onEdit={canManage ? emp => navigate("edit", emp) : null}
                onDelete={canManage ? emp => setConfirmDelete(emp) : null}
                token={token}
                dropdowns={dropdowns}
              />
            )}
            {(view === "create" || view === "edit") && (
              <EmployeeForm
                employee={selected}
                mode={view}
                onBack={() => navigate("list")}
                onSave={handleSave}
                token={token}
                dropdowns={dropdowns}
              />
            )}
          </main>
        </div>
      </MainLayout>

      <ConfirmModal
        isOpen={!!confirmDelete}
        title="Delete Employee"
        message={`Are you sure you want to permanently delete ${confirmDelete ? fullName(confirmDelete) : "this employee"}? This action cannot be undone.`}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDelete(null)}
        confirmLabel={isDeleting ? "Deleting..." : "Delete Permanently"}
      />
    </>
  );
}